import { DEFINED_BENEFIT_FUNDS, DEFAULT_DB_INDEXATION_PCT, annualFromPayment, definedBenefitFundPreset, describeDefinedBenefitTreatment, type DefinedBenefitFundId } from '../../../../shared/src/calculation/definedBenefitPension.ts';
import type { OtherIncomeItem } from '../../../../shared/src/calculation/factFindTypes.ts';
import React, { useState, useMemo } from 'react';
import { holdsHomeNobodyLivesIn, anyoneOwnsHome } from '../../../../shared/src/calculation/householdPosition.ts';
import { isCapitalisingLoan, capitalisingLoanRateOf } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import type { LucideIcon } from 'lucide-react';
import { Calculator, Trash2, PlusCircle, AlertTriangle, ChevronDown, Info, Check, User, UserCheck, Target, Home, Wallet, CreditCard, Banknote, Users, KeyRound, Building2, HeartPulse } from 'lucide-react';
import { ACAT_LEVEL_DATA, ASSET_TYPES, LIABILITY_TYPES, OTHER_INCOME_TYPES, STEP4_FIELD_TOOLTIPS, ASFA_BUDGET, GOAL_INSIGHTS } from '../../constants/index.ts'
import { SAH_CLASSIFICATION_KEYS, TRANSITIONED_HCP_LEVEL_KEYS } from '../../../../shared/src/calculation/constants/acatLevelData.ts';
import { buildNewAsset, applyStatementImport } from './buildNewAsset.tsx';
import type { DraftAsset } from './buildNewAsset.tsx';
import AssetStatementImportModal from '../../components/modals/AssetStatementImportModal.tsx';
import type { StatementImportDraft } from './statementImportStub.tsx';
import type { ImportedLiabilityDraft } from '../../../../shared/src/calculation/statementImport.ts';
import { GIFTING_LIMIT_ANNUAL, GIFTING_LIMIT_5_YEAR, PENSION_RATES, EXTRA_ALLOWABLE_AMOUNT } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { VILLAGE_REFUND_MONTHS_BY_STATE } from '../../../../shared/src/calculation/support/villageExit.ts';
import { calculateDeprivedAssetsFromGifts } from '../../../../shared/src/calculation/giftingDeprivation.ts';
import { getGrannyFlatDeprivationFromFactFind } from '../../../../shared/src/calculation/grannyFlatArrangement.ts';
import { FormInput, CurrencyInput, NumberInput, PercentageInput, FormSelect, FormCheckbox, FormRadioGroup, InfoIcon, InsightCard, IconTile } from '../../components/ui/index.tsx';
import MyAgedCareApplyLink from '../../components/branding/MyAgedCareApplyLink.tsx';
import { calculateAge, formatCurrency, toYesNo, fromYesNo, toTriYesNo, fromTriYesNo } from '../../utils/index.ts';
import type { ApplicantVoice } from '../../utils/index.ts';
import { newEntityId } from '../../utils/entityId.ts';
import { calculateLoanRepayment } from '../../../../shared/src/calculation/taxAndLoanUtils.ts';
import { describeCgtDataGap, getAssetCgtDataGap } from '../../../../shared/src/calculation/cgtDataQuality.ts';
import { partnerRemainsInFamilyHome } from '../../../../shared/src/calculation/householdStatus.ts';
import { INVESTMENT_PROFILES, INVESTMENT_PROFILE_IDS, DEFAULT_INVESTMENT_PROFILE, superPresetFor, profileForSuperRate } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId, SuperWrapper } from '../../../../shared/src/calculation/investmentProfiles.ts';
import PensionMinimumWarning from '../../components/PensionMinimumWarning.tsx';
import RentalExpensesCalculator from '../../components/RentalExpensesCalculator.tsx';
import BudgetCalculator from '../../components/BudgetCalculator.tsx';
import ClientStructuresSection from './ClientStructuresSection.tsx';
import { abpOwnerAge } from '../../components/pensionMinimum.ts';
import { AssessmentForkNote, ChspComparisonModal } from '../../components/care/EntryLevelSupportExplainer.tsx';
import { entityOwnershipValue, isEntityOwnership } from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import {
    buildOwnershipOptions,
    derivesRepaymentAutomatically,
    getStructureLiabilityWarnings,
    isTermLoanType,
} from '../../utils/structureLiabilitiesUtils.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type {
    FactFind,
    FactFindPerson,
    FactFindHome,
    AcatPerson,
    AssetType,
    FactFindAsset,
    FactFindLiability,
} from '../../../../shared/src/calculation/factFindTypes.ts';

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
import { stockPhoto } from '../../assets/stock/index.ts';
type DraftPerson = FactFindPerson;
type DraftHome = FactFindHome;
type DraftAcatPerson = AcatPerson;
type DraftLiability = FactFindLiability;

/** Generic per-section field setter shared by every fact-find step: (section, field, value). */
type UpdateFactFind = (section: string, field: string, value: unknown) => void;
type SetFactFind = React.Dispatch<React.SetStateAction<DraftFactFind>>;

/**
 * Elevated, consistent step header (brand IconTile + step eyebrow + title) —
 * replaces the plain grey "Step N: ..." h2 so the fact-find matches the rest of the app.
 */
interface StepHeaderProps {
    icon: LucideIcon;
    step: number;
    title: string;
}

/**
 * A living-arrangement goal's label, carrying the SAME icon as the insight card that appears
 * beside it once the box is ticked — both read `GOAL_INSIGHTS[key].icon`, so the row and its
 * explanation can never drift apart.
 *
 * Only the living-arrangement group gets these. They are concrete, depictable places; the
 * financial goals below them ("Preserve Assets For Inheritance") are abstract, and inventing
 * pictures for them is where an interface starts looking generic. Icons on one group and not the
 * other also does useful work: it separates places to live from priorities to weigh.
 */
const goalLabel = (goalKey: string, text: string): React.ReactNode => {
    const Icon = GOAL_INSIGHTS[goalKey]?.icon;
    if (!Icon) return text;
    return (
        <span className="flex items-center gap-2 min-w-0">
            <Icon size={16} className="text-blue-600 shrink-0" aria-hidden="true" />
            <span className="min-w-0">{text}</span>
        </span>
    );
};

/**
 * The age a date of birth works out to, shown right under the field.
 *
 * Asked for by an adviser: a date of birth is what the form collects, but age is what the
 * conversation and the rules are actually about — pension age, the drawdown brackets, means-test
 * thresholds — and doing the arithmetic in your head for two people is friction the tool can
 * remove. Silent until the date is complete and sane, so it never flickers a stray number while a
 * year is being typed.
 */
const AgeHint = ({ dob }: { dob?: string }) => {
    if (!dob) return null;
    const age = calculateAge(dob);
    if (age === null || !Number.isFinite(age) || age < 0 || age > 120) return null;
    return <p className="mt-1 text-sm text-gray-500">Age {age}</p>;
};

const StepHeader = ({ icon, step, title }: StepHeaderProps) => (
    <div className="flex items-center gap-4 mb-6">
        <IconTile icon={icon} tone="brand" variant="solid" size="lg" />
        <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-600/80">Step {step}</p>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">{title}</h2>
        </div>
    </div>
);

/**
 * Photographs for the "who is completing this form?" cards, hotlinked from Unsplash the same way
 * MainAppLayout sources its page backdrops. Each was chosen to be recognisable at a glance from
 * across the room — the whole point of this step is that the answer is picked deliberately rather
 * than left on the default of a dropdown nobody read.
 */
const completerPhoto = (id: string): string =>
    stockPhoto(id, 640, 420);

interface CompleterOption {
    /** Stored on `factFind.completer` — these values are what every other screen branches on. */
    id: 'client' | 'family' | 'eop' | 'professional';
    label: string;
    description: string;
    image: string;
}

/* Photos are decorative (`alt=""`): the label and description already say what each card means, and
   describing the picture as well would read out ahead of the choice on every radio. */
const COMPLETER_OPTIONS: CompleterOption[] = [
    {
        id: 'client',
        label: 'I am the person seeking advice',
        description: 'Planning for yourself, or for you and your partner together.',
        image: completerPhoto('1658632302217-984d432b4d38'), // an older couple together at home
    },
    {
        id: 'family',
        label: 'I am a family member',
        description: 'A son, daughter or other relative helping someone you care about.',
        image: completerPhoto('1741651366517-cf81c7de3868'), // an adult daughter beside her elderly mother
    },
    {
        id: 'eop',
        // Reworded because a reader who was BOTH a son and his father's attorney could not tell
        // which card was his (client meeting, 2026-08-20). Naming the family case here, and the
        // tie-break line under the grid, settles it: the formal role wins.
        label: 'I hold Enduring Power of Attorney',
        description: 'A relative or other person formally authorised to make financial decisions.',
        image: completerPhoto('1664463760781-f159dfe3af30'), // signing a legal document across a desk
    },
    {
        id: 'professional',
        label: 'I am a professional',
        description: 'An adviser, accountant, lawyer or care manager acting for a client.',
        image: completerPhoto('1551836022-aadb801c60ae'), // an adviser and a client at a meeting table
    },
];

interface Step1WhoIsCompletingProps {
    factFind: DraftFactFind;
    setMainData: (field: string, value: string | number) => void;
}

/**
 * Step 1 — who is filling this in. Its own step because the answer changes every screen that
 * follows: it flips the whole fact-find between "your home" and "Bob & Sally's home" (see
 * utils/applicantVoice.ts), and drives the estate-planning and mental-capacity prompts on the last
 * step. As a dropdown among other Setup fields it was easy to miss and easy to leave on its
 * default, so the fact-find spoke to a daughter as though she were her mother.
 */
const Step1_WhoIsCompleting = ({ factFind, setMainData }: Step1WhoIsCompletingProps) => {
    const selected = factFind.completer || 'client';

    return (
        <div>
            <StepHeader icon={UserCheck} step={1} title="Who Is Completing This Form?" />
            <div className="space-y-8">
                <fieldset>
                    <legend className="sr-only">Who is completing this form?</legend>
                    <p className="text-gray-600 mb-3 max-w-2xl">
                        Everything that follows is worded around this answer, so it's worth getting right. You can
                        change it at any time by coming back to this step.
                    </p>
                    <p className="text-sm text-gray-500 mb-6 max-w-2xl">
                        More than one fits? Choose the one with the formal role &mdash; Enduring Power of Attorney if
                        you hold it.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {COMPLETER_OPTIONS.map((option) => {
                            const isSelected = selected === option.id;
                            return (
                                <label key={option.id} className="block cursor-pointer">
                                    {/* A real radio, visually hidden: keyboard, arrow keys and screen readers all
                                        behave natively, and the card is just its (very large) label. */}
                                    <input
                                        type="radio"
                                        name="completer"
                                        value={option.id}
                                        checked={isSelected}
                                        onChange={() => setMainData('completer', option.id)}
                                        className="peer sr-only"
                                    />
                                    <div
                                        className={`h-full overflow-hidden rounded-2xl border-2 bg-white transition-all duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-[#0290F5] peer-focus-visible:ring-offset-2 ${
                                            isSelected
                                                ? 'border-[#0F52E0] shadow-cardlg'
                                                : 'border-slate-200 shadow-card hover:border-slate-300 hover:shadow-cardlg'
                                        }`}
                                    >
                                        <div className="relative bg-gradient-to-br from-slate-100 to-slate-200">
                                            <img
                                                src={option.image}
                                                alt=""
                                                loading="lazy"
                                                className="h-40 w-full object-cover sm:h-44"
                                                // A blocked or failed photo should leave the soft gradient behind it,
                                                // not a broken-image glyph — the card still reads fine without it.
                                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                            />
                                            {isSelected && (
                                                <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#0F52E0] text-white shadow-md">
                                                    <Check size={18} strokeWidth={3} />
                                                </span>
                                            )}
                                        </div>
                                        <div className="p-4">
                                            <p className={`text-base font-semibold leading-snug ${isSelected ? 'text-[#001E7A]' : 'text-gray-900'}`}>
                                                {option.label}
                                            </p>
                                            <p className="mt-1 text-sm text-gray-600">{option.description}</p>
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </fieldset>
                {selected !== 'client' && (
                    <fieldset>
                        <legend className="text-lg font-semibold text-gray-700 mb-4">Your Details (as the person completing this form)</legend>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <FormInput label="Your First Name" name="completerFirstName" value={factFind.completerFirstName || ''} onChange={(n,v) => setMainData(n,v)} />
                            <FormInput label="Your Last Name" name="completerLastName" value={factFind.completerLastName || ''} onChange={(n,v) => setMainData(n,v)} />
                            <FormInput label="Your Relationship to Applicant" name="completerRelationship" value={factFind.completerRelationship || ''} onChange={(n,v) => setMainData(n,v)} />
                        </div>
                    </fieldset>
                )}
            </div>
        </div>
    );
};

interface Step2YourDetailsProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    setMainData: (field: string, value: string | number) => void;
    isPartnered: boolean;
    hideApplicantContactFields?: boolean;
    voice: ApplicantVoice;
}

const Step2_YourDetails = ({ factFind, updateFactFind, setMainData, isPartnered, hideApplicantContactFields = true, voice }: Step2YourDetailsProps) => {
    /*
     * TRI-STATE, not defaulted to No (owner, 23 Sep 2026). Private cover decides whether the Medicare
     * levy surcharge applies, so a seeded "No" answered a tax question on the client's behalf and put
     * a figure in their cashflow they never stated. Unanswered now shows as unanswered; the engine
     * still models no cover until told otherwise, which is the conservative direction, and the
     * Assumptions page discloses it.
     */
    const handleCouplePhiChange = (name: string, value: string) => {
        const phi = fromTriYesNo(value);
        updateFactFind('client', 'phi', phi);
        if (isPartnered) {
            updateFactFind('partner', 'phi', phi);
        }
    };

    return (
    <div>
        <StepHeader icon={User} step={2} title={`${voice.Possessive} Details`} />
        <div className="space-y-8">
            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">Setup</legend>
                <FormSelect label="Who are we providing advice for?" name="adviceFor" value={factFind.adviceFor || 'single'} onChange={(n, val) => setMainData(n, val)}>
                    <option value="single">A single person</option>
                    <option value="couple">A member of a couple</option>
                </FormSelect>
            </fieldset>
            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">{voice.Possessive} Details</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <FormInput label="First Name" name="firstName" value={factFind.client?.firstName || ''} onChange={(n,v) => updateFactFind('client', n, v)} />
                    <FormInput label="Last Name" name="lastName" value={factFind.client?.lastName || ''} onChange={(n,v) => updateFactFind('client', n, v)} />
                    {!hideApplicantContactFields && (
                        <>
                            <FormInput label="Email" name="email" type="email" value={factFind.client?.email || ''} onChange={(n,v) => updateFactFind('client', n, v)} placeholder="Optional" />
                            <FormInput label="Preferred phone number" name="preferredPhone" type="tel" value={factFind.client?.preferredPhone || ''} onChange={(n,v) => updateFactFind('client', n, v)} />
                        </>
                    )}
                    <div>
                        <FormInput label="Date of Birth" name="dob" type="date" value={factFind.client?.dob || ''} onChange={(n,v) => updateFactFind('client', n, v)} />
                        <AgeHint dob={factFind.client?.dob} />
                    </div>
                    {/* Read only by the life-expectancy rules on a lifetime annuity (the capital access
                        schedule) — the Government Actuary's tables are drawn by sex. Optional: blank
                        uses the shorter table, and the annuity screens say so. */}
                    <div>
                        <FormSelect label="Sex" name="sex" value={factFind.client?.sex || ''} onChange={(n,v) => updateFactFind('client', n, v)}>
                            <option value="">Prefer not to say</option>
                            <option value="female">Female</option>
                            <option value="male">Male</option>
                        </FormSelect>
                        <p className="text-xs text-gray-500 mt-1">Only used for the life-expectancy rules that apply to a lifetime annuity. Optional.</p>
                    </div>
                    {/* APP 3.3: health is sensitive information — collected only with consent, and optional. */}
                    <p className="text-xs text-gray-500 md:col-span-2 -mb-2">
                        Health details are sensitive information and optional. {voice.Possessive} health helps us decide whether
                        strategies that take years to pay off are worth considering. Leave it blank if {voice.subject} would
                        rather not share it, and no judgement is made on it.
                    </p>
                    <FormSelect label="Health Status" name="healthStatus" value={factFind.client?.healthStatus || ''} onChange={(n,v) => updateFactFind('client', n, v)}>
                        <option value="">Select...</option>
                        <option value="good">Good - Independent with no major health concerns.</option>
                        <option value="fair">Fair - Some health issues, may need minor assistance.</option>
                        <option value="poor">Poor - Significant health issues requiring regular assistance.</option>
                    </FormSelect>
                    <FormRadioGroup label="Has Private Health Insurance?" name="clientPhi" value={toTriYesNo(factFind.client?.phi)} onChange={handleCouplePhiChange} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                </div>
            </fieldset>
            {isPartnered && (
                 <fieldset>
                    <legend className="text-lg font-semibold text-gray-700 mb-4">Partner's Details</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <FormInput label="Partner's First Name" name="firstName" value={factFind.partner?.firstName || ''} onChange={(n,v) => updateFactFind('partner', n, v)} />
                        <FormInput label="Partner's Last Name" name="lastName" value={factFind.partner?.lastName || ''} onChange={(n,v) => updateFactFind('partner', n, v)} />
                        <div>
                            <FormInput label="Partner's Date of Birth" name="dob" type="date" value={factFind.partner?.dob || ''} onChange={(n,v) => updateFactFind('partner', n, v)} />
                            <AgeHint dob={factFind.partner?.dob} />
                        </div>
                        <div>
                            <FormSelect label="Partner's Sex" name="sex" value={factFind.partner?.sex || ''} onChange={(n,v) => updateFactFind('partner', n, v)}>
                                <option value="">Prefer not to say</option>
                                <option value="female">Female</option>
                                <option value="male">Male</option>
                            </FormSelect>
                            <p className="text-xs text-gray-500 mt-1">Only used for the life-expectancy rules that apply to a lifetime annuity. Optional.</p>
                        </div>
                        <FormSelect label="Partner's Health Status" name="healthStatus" value={factFind.partner?.healthStatus || ''} onChange={(n,v) => updateFactFind('partner', n, v)}>
                            <option value="">Select...</option>
                            <option value="good">Good - Independent with no major health concerns.</option>
                            <option value="fair">Fair - Some health issues, may need minor assistance.</option>
                            <option value="poor">Poor - Significant health issues requiring regular assistance.</option>
                        </FormSelect>
                         <FormRadioGroup label="Has Private Health Insurance?" name="partnerPhi" value={toTriYesNo(factFind.partner?.phi)} onChange={handleCouplePhiChange} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                    </div>
                </fieldset>
            )}
        </div>
    </div>
    );
};

interface AcatAssessmentSectionProps {
    person: 'clientAcat' | 'partnerAcat';
    personData: DraftAcatPerson | undefined;
    updatePersonAcat: (person: 'clientAcat' | 'partnerAcat', field: string, value: unknown) => void;
    personName: string;
    voice: ApplicantVoice;
}

const AcatAssessmentSection = ({ person, personData, updatePersonAcat, personName, voice }: AcatAssessmentSectionProps) => {
    /*
     * "No" is the most consequential answer on this step, and until 2026-08-28 it was also the
     * deadest: it recorded a fact and moved on. The client then met the Support at Home module with
     * no idea what was about to be asked of them, and no idea an entry-level program exists at all.
     *
     * The fork panel below is shown for BOTH 'no' and 'in_progress' — someone mid-assessment has
     * exactly the same question and exactly the same unknown answer.
     */
    const [compareOpen, setCompareOpen] = useState(false);
    const awaitingAssessment = personData?.status === 'no' || personData?.status === 'in_progress';
    // This section is already per-person, so third person names the individual rather than the
    // couple — "Was Sally in receipt of…", not "Were Bob & Sally in receipt of…".
    const who = voice.isThirdPerson ? personName : 'you';
    const whose = voice.isThirdPerson ? `${personName}'s` : 'your';
    const wasWere = voice.isThirdPerson ? 'Was' : 'Were';
    const handleHomeCareChange = (level: string) => {
        updatePersonAcat(person, 'homeCareLevel', level);
    };

    const handleOtherCareChange = (type: string, isChecked: boolean) => {
        const currentRecommendation = personData?.recommendation || {};
        updatePersonAcat(person, 'recommendation', { ...currentRecommendation, [type]: isChecked });
    };

    return (
        <div className="mt-4 pt-4 border-t border-dashed">
            <p className="font-semibold text-gray-800 mb-2">{personName}'s Assessment</p>
            <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Has an assessment been completed?</label>
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                    <div className="flex flex-wrap items-center gap-4">
                        {[
                            { label: 'Yes', value: 'yes' },
                            { label: 'No', value: 'no' },
                            { label: 'In Progress', value: 'in_progress' },
                        ].map((opt) => (
                            <label key={opt.value} className="flex cursor-pointer items-center gap-2">
                                <input
                                    type="radio"
                                    name={`status_${person}`}
                                    value={opt.value}
                                    checked={personData?.status === opt.value}
                                    onChange={(e) => updatePersonAcat(person, 'status', e.target.value)}
                                    className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-gray-700">{opt.label}</span>
                            </label>
                        ))}
                    </div>
                    {awaitingAssessment && (
                        <MyAgedCareApplyLink status={personData!.status!} className="self-start sm:ml-auto" />
                    )}
                </div>
            </div>
            {awaitingAssessment && (
                <>
                    <AssessmentForkNote
                        who={voice.isThirdPerson ? personName : undefined}
                        onCompare={() => setCompareOpen(true)}
                        className="mt-4"
                    />
                    <ChspComparisonModal open={compareOpen} onClose={() => setCompareOpen(false)} />
                </>
            )}
            {personData?.status === 'yes' && (
                <div className="mt-4 space-y-4">
                    {/* Home Care Package Transition Question - ASKED FIRST */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <FormRadioGroup
                            label={
                                <span>
                                    Before 13 September 2024, {wasWere} {who} already getting government-funded care at home (a Home Care Package), or approved for one — including being on the waiting list?
                                    <InfoIcon text={`Most people can answer No. Answer Yes only if ${who} had a Home Care Package, or had been approved for one at the aged care assessment, by 12 September 2024 — even if the package never arrived because ${voice.subject} ${voice.isThirdPerson ? 'was' : 'were'} still on the waiting list. The approval date is on the letter from My Aged Care after the assessment, or in the My Aged Care online account; My Aged Care (1800 200 422) can confirm it. Why we ask: under the Department's 'no worse off' principle it changes which fees apply — in Support at Home and in an aged care home — and usually lowers them. If you choose Unsure we use the standard rules, which are usually higher, and you can update this later.`} />
                                </span>
                            }
                            name={`hcpBeforeSep2024_${person}`}
                            value={personData.hcpBeforeSep2024 || ''}
                            onChange={(n, v) => updatePersonAcat(person, n.replace(`_${person}`, ''), v)}
                            options={[
                                {label: 'Yes', value: 'yes'},
                                {label: 'No', value: 'no'},
                                {label: 'Unsure', value: 'unsure'}
                            ]}
                        />
                        {personData.hcpBeforeSep2024 === 'unsure' && (
                            <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-300">
                                <p className="text-xs text-blue-800">
                                    No problem — we&apos;ll use the standard rules, which are usually the higher estimate, for Support at Home
                                    and for an aged care home. The approval date is on the letter from My Aged Care after the assessment, or you
                                    can call My Aged Care on 1800 200 422; update this any time.
                                </p>
                            </div>
                        )}
                        {personData.hcpBeforeSep2024 === 'yes' && (
                            <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-300">
                                <p className="text-xs text-blue-900 mb-2 font-semibold">What this means</p>
                                <p className="text-xs text-blue-800 mb-3">
                                    {voice.isThirdPerson ? personName : 'You'} {voice.isThirdPerson ? 'is' : 'are'} covered by the Department&apos;s &lsquo;no worse off&rsquo; principle. In Support at Home
                                    that means reduced contribution rates (or none); in an aged care home it means the means-tested care fee instead of the
                                    hotelling and non-clinical care contributions. Two more details let the tool work out the exact figures — both are on
                                    the letters Services Australia sent about the package.
                                </p>
                                <FormRadioGroup
                                    label={`Under the Home Care Package, ${voice.isThirdPerson ? 'was' : 'were'} ${who} asked to pay an income-tested care fee? (It is named on Services Australia's fee letter for the package and on the provider's monthly statement.)`}
                                    name={`hcpCategory_${person}`}
                                    value={personData.hcpCategory || ''}
                                    onChange={(n, v) => updatePersonAcat(person, n.replace(`_${person}`, ''), v)}
                                    options={[
                                        {label: 'No — no income-tested care fee (nothing, or only the basic daily fee)', value: 'A'},
                                        {label: 'Yes — an income-tested care fee of $1 a day or more', value: 'B'}
                                    ]}
                                />
                                {personData.hcpCategory === 'B' && (
                                    <div className="mt-3">
                                        <label className="block text-xs font-medium text-blue-900 mb-1">
                                            The income-tested care fee under the package, per day (from the Services Australia fee letter or the provider&apos;s statement):
                                        </label>
                                        <CurrencyInput
                                            value={personData.previousITCF || ''}
                                            onChange={(v) => updatePersonAcat(person, 'previousITCF', v ?? 0)}
                                            className="text-sm"
                                            placeholder="e.g., 37.70"
                                        />
                                    </div>
                                )}
                                <div className="mt-3">
                                    <label className="block text-xs font-medium text-blue-900 mb-1">
                                        Care contributions already paid, in total (income-tested care fees under the package and any Support at Home contributions) — optional:
                                    </label>
                                    <CurrencyInput
                                        value={personData.priorCareContributionsPaid || ''}
                                        onChange={(v) => updatePersonAcat(person, 'priorCareContributionsPaid', v ?? 0)}
                                        className="text-sm"
                                        placeholder="e.g., 4,200"
                                    />
                                    <p className="mt-1 text-xs text-blue-800">
                                        Services Australia keeps this running total and states it in its letters. It counts towards the lifetime caps on care
                                        fees in Support at Home and in a home; leave it blank if you don&apos;t know it.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    {/*
                      * TWO KINDS OF FUNDING LEVEL (Department of Health, "Funding classifications for Support
                      * at Home", 1 July 2026). A Home Care Package approved before 1 November 2025 — held or
                      * still on the waiting list — became a transitioned Support at Home level with the
                      * package's own funding (levels 1–4), and stays so until a new assessment places the
                      * person in one of the eight classifications. Asked in plain words, defaulted from the
                      * package question above, and the dropdown only ever lists the levels that fit.
                      */}
                    {(() => {
                        const levelBasis: 'transitioned_hcp' | 'sah_classification' =
                            personData.levelBasis ?? (personData.hcpBeforeSep2024 === 'yes' ? 'transitioned_hcp' : 'sah_classification');
                        const keys = levelBasis === 'transitioned_hcp' ? TRANSITIONED_HCP_LEVEL_KEYS : SAH_CLASSIFICATION_KEYS;
                        const current = personData.homeCareLevel || '';
                        const setBasis = (basis: string) => {
                            updatePersonAcat(person, 'levelBasis', basis);
                            const allowed = basis === 'transitioned_hcp' ? TRANSITIONED_HCP_LEVEL_KEYS : SAH_CLASSIFICATION_KEYS;
                            if (current && !allowed.includes(current)) updatePersonAcat(person, 'homeCareLevel', '');
                        };
                        return (
                            <div className="space-y-4" data-testid={`level-basis-${person}`}>
                                <FormRadioGroup
                                    label={
                                        <span>
                                            What kind of home care approval does {who} have?{' '}
                                            <InfoIcon text={`A Home Care Package approved before 1 November 2025 — whether ${who} had started receiving it or ${voice.isThirdPerson ? 'was' : 'were'} still on the waiting list — became a 'transitioned' Support at Home level with the same funding as the package (levels 1 to 4). It stays that way until a new aged care assessment, which places ${who} in one of the eight Support at Home classifications. The level is on the My Aged Care approval letter; a classification is on the Notice of Decision from an assessment on or after 1 November 2025.`} />
                                        </span>
                                    }
                                    name={`levelBasis_${person}`}
                                    value={levelBasis}
                                    onChange={(n, v) => setBasis(String(v))}
                                    options={[
                                        { label: 'A Home Care Package (level 1–4) approved before 1 November 2025, with no new assessment since', value: 'transitioned_hcp' },
                                        { label: 'A Support at Home classification (1–8) from an assessment on or after 1 November 2025', value: 'sah_classification' },
                                    ]}
                                />
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {levelBasis === 'transitioned_hcp'
                                            ? `Home Care Package level (the package ${who} had, or ${voice.isThirdPerson ? 'was' : 'were'} approved for)`
                                            : 'Approved Support at Home classification (on the Notice of Decision)'}
                                    </label>
                                    <FormSelect name="homeCareLevel" value={current} onChange={(n, v) => handleHomeCareChange(v)}>
                                        <option value="">None</option>
                                        {keys.map((key) => (
                                            <option key={key} value={key}>{ACAT_LEVEL_DATA[key]!.label} — {formatCurrency(ACAT_LEVEL_DATA[key]!.budget)} a year</option>
                                        ))}
                                    </FormSelect>
                                    {levelBasis === 'transitioned_hcp' && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Carried into Support at Home with the package&apos;s own funding. If {who} {voice.isThirdPerson ? 'has' : 'have'} since been reassessed, choose the classification option above instead.
                                        </p>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                    
                    <FormCheckbox label="Approved for Residential Aged Care" name="residential" checked={personData.recommendation?.residential} onChange={(n,v) => handleOtherCareChange(n,v)} />
                    <FormCheckbox label="Approved for Residential Respite Care" name="respite" checked={personData.recommendation?.respite} onChange={(n,v) => handleOtherCareChange(n,v)} />
                </div>
            )}
        </div>
    );
};

interface Step3GoalsCareNeedsProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    isPartnered: boolean;
    voice: ApplicantVoice;
}

interface CareGoalOption {
    id: 'home_care' | 'aged_care';
    label: string;
}

interface FinancialGoalOption {
    id: 'meet_costs' | 'maximise_support' | 'preserve_assets' | 'funding_guidance';
    label: string;
}

const Step3_GoalsCareNeeds = ({ factFind, updateFactFind, isPartnered, voice }: Step3GoalsCareNeedsProps) => {
    const { goals, client, partner } = factFind;

    const careGoals: CareGoalOption[] = [
        { id: 'home_care', label: 'Access Home Care Support' },
        { id: 'aged_care', label: 'Move into residential aged care' },
    ];

    const financialGoals: FinancialGoalOption[] = [
        { id: 'meet_costs', label: 'Meet Care Costs'},
        { id: 'maximise_support', label: 'Maximise Government Support'},
        { id: 'preserve_assets', label: 'Preserve Assets For Inheritance'},
        { id: 'funding_guidance', label: 'Guidance On Funding Options'},
    ];

    const selectedCareGoals = careGoals.filter(goal => goals?.[goal.id]);

    const updateCareNeeds = (person: 'clientAcat' | 'partnerAcat', field: string, value: unknown) => {
        const currentPersonAcat = factFind.careNeeds?.[person] || { recommendation: {} };
        updateFactFind('careNeeds', person, { ...currentPersonAcat, [field]: value });
    };

    return (
        <div>
            <StepHeader icon={Target} step={3} title="Goals" />
            <div className="space-y-8">
                <fieldset>
                    <legend className="text-lg font-semibold text-gray-700 mb-4">Primary Living Arrangement Goals</legend>
                    <p className="text-gray-600 mb-4">What would {voice.subject} like to explore? (Select all that apply)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormCheckbox label={goalLabel('home_care', 'Access Home Care Support')} name="home_care" checked={goals?.home_care || false} onChange={(n,v) => updateFactFind('goals',n,v)} />
                        <FormCheckbox label={goalLabel('downsize_home', 'Downsize the family home')} name="downsize_home" checked={goals?.downsize_home || false} onChange={(n,v) => updateFactFind('goals',n,v)} />
                        <FormCheckbox label={goalLabel('retirement_village', 'Move to a retirement village')} name="retirement_village" checked={goals?.retirement_village || false} onChange={(n,v) => updateFactFind('goals',n,v)} />
                        <FormCheckbox label={goalLabel('granny_flat', 'Explore granny flat options')} name="granny_flat" checked={goals?.granny_flat || false} onChange={(n,v) => updateFactFind('goals',n,v)} />
                        <FormCheckbox label={goalLabel('aged_care', 'Move into residential aged care')} name="aged_care" checked={goals?.aged_care || false} onChange={(n,v) => updateFactFind('goals',n,v)} />
                    </div>
                </fieldset>

                <fieldset>
                    <legend className="text-lg font-semibold text-gray-700 mb-4">Financial Goals</legend>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {financialGoals.map(goal => (
                           <FormCheckbox key={goal.id} label={goal.label} name={goal.id} checked={goals?.[goal.id] || false} onChange={(n,v) => updateFactFind('goals',n,v)} />
                       ))}
                    </div>
                </fieldset>

                {isPartnered && selectedCareGoals.length > 0 && (
                    <fieldset>
                        <legend className="text-lg font-semibold text-gray-700 mb-4">Care Goal Details</legend>
                        <div className="space-y-4">
                            {selectedCareGoals.map(goal => (
                                <FormRadioGroup
                                    key={goal.id}
                                    label={`Who is the goal "${goal.label}" for?`}
                                    name={`${goal.id}_for`}
                                    value={goals?.[`${goal.id}_for`] || ''}
                                    onChange={(n,v) => updateFactFind('goals',n,v)}
                                    options={[
                                        {label: client?.firstName || 'Applicant 1', value: 'client'},
                                        {label: partner?.firstName || 'Applicant 2', value: 'partner'},
                                        {label: 'Both', value: 'both'},
                                    ]}
                                />
                            ))}
                        </div>
                    </fieldset>
                )}

                {/* Care assessment only applies to a care journey (home care or residential aged care). Clients
                    exploring only a retirement village / granny flat / downsizing don't see it. */}
                {selectedCareGoals.length > 0 && (
                    <fieldset>
                        <legend className="text-lg font-semibold text-gray-700 mb-4">Care assessment (aged care needs assessment)</legend>
                        <AcatAssessmentSection
                            person="clientAcat"
                            personData={factFind.careNeeds?.clientAcat}
                            updatePersonAcat={updateCareNeeds}
                            personName={client?.firstName || 'the applicant'}
                            voice={voice}
                        />
                        {isPartnered && (
                            <AcatAssessmentSection
                                person="partnerAcat"
                                personData={factFind.careNeeds?.partnerAcat}
                                updatePersonAcat={updateCareNeeds}
                                personName={partner?.firstName || 'Partner'}
                                voice={voice}
                            />
                        )}
                    </fieldset>
                )}
            </div>
        </div>
    );
};

interface Step4FamilyHomeProps {
    home: DraftHome;
    updateFactFind: (field: string, value: unknown) => void;
    client: DraftPerson | undefined;
    partner: DraftPerson | undefined;
    isPartnered: boolean;
    /** The whole fact-find, read only to answer "will a partner be left in this home?" — see below. */
    factFind: DraftFactFind;
    onHomeTitleOwnershipChange?: (value: string) => void;
    voice: ApplicantVoice;
    /** False when the living-situation chooser above has already rendered the step header. */
    showHeader?: boolean;
}

const Step4_FamilyHome = ({ home, updateFactFind, client, partner, isPartnered, factFind, onHomeTitleOwnershipChange, voice, showHeader = true }: Step4FamilyHomeProps) => {
    const [showRentalCalculator, setShowRentalCalculator] = useState(false);

    /**
     * Whether to ASK who will stay in the home — and the answer is no whenever a partner is staying.
     *
     * Deliberately the same function the means test uses (`partnerRemainsInFamilyHome`), rather than
     * a second reading of the care goal here. If the form decided this on its own the two could drift,
     * and the failure would be silent: a question hidden on the belief the home is exempt, while the
     * engine assessed it anyway. Asking the same function both times makes that impossible.
     *
     * When a partner IS staying, the answer is already known — they are the protected person — so the
     * question has nothing to add and its natural-sounding "No" is simply wrong.
     */
    const partnerStayingHome = partnerRemainsInFamilyHome(factFind as FactFind);


    // Get client and partner names with fallbacks
    const clientName = client?.firstName || 'Client';
    const partnerName = partner?.firstName || 'Partner';

    return (
    <div>
        {showHeader
            ? <StepHeader icon={Home} step={4} title={voice.isThirdPerson ? `${voice.Possessive} Home` : 'The Family Home'} />
            : <h3 className="text-xl font-bold text-gray-900 mb-6 mt-8 pt-8 border-t border-slate-200">{voice.isThirdPerson ? `${voice.Possessive} Home` : 'The Family Home'}</h3>}
        <div className="space-y-8">
            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">Home Value & Growth</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <CurrencyInput label="Estimated Market Value ($)" name="value" value={home.value || ''} onChange={(v) => updateFactFind('value', v)} />
                    <PercentageInput
                        label={<span>Assumed Annual Growth Rate (%) <InfoIcon text={STEP4_FIELD_TOOLTIPS.homeGrowthRate} /></span>}
                        name="homeGrowthRate"
                        value={home.homeGrowthRate || ''}
                        onChange={(v) => updateFactFind('homeGrowthRate', v)}
                    />
                </div>
                <div className="mt-4">
                    <FormSelect
                        label={
                            <span>
                                Home owner on title records{' '}
                                <InfoIcon text={`Who is named on the property title? This affects Age Pension and care fee estimates. If ${voice.possessive} home is in a family trust or company name, choose Other entity — you'll add details on the next step.`} />
                            </span>
                        }
                        name="ownership"
                        value={home.ownership || (isPartnered ? 'joint' : 'client')}
                        onChange={(n, v) => {
                            updateFactFind(n, v);
                            onHomeTitleOwnershipChange?.(v);
                        }}
                    >
                        {!isPartnered && (
                            <option value="client">{clientName} only</option>
                        )}
                        {isPartnered && (
                            <>
                                <option value="joint">Jointly held (50/50)</option>
                                <option value="client">{clientName} only</option>
                                <option value="partner">{partnerName} only</option>
                            </>
                        )}
                        <option value="other_entity">Other entity (trust, company, etc.)</option>
                    </FormSelect>
                    {home.ownership === 'other_entity' && (
                        <>
                            <div className="flex gap-2 mt-3 p-3 rounded-lg bg-teal-50 border border-teal-200 text-sm text-teal-900">
                                <Info size={18} className="shrink-0 mt-0.5" />
                                <span>
                                    You&apos;ll add the trust or company name on the next step (Assets) and confirm which one owns this home.
                                </span>
                            </div>
                            <div className="mt-4">
                                <FormRadioGroup
                                    label={
                                        <span>
                                            {voice.verb('Do', 'Does')} {voice.subject} have a secure right to keep living in this home?{' '}
                                            <InfoIcon text={`Centrelink treats the home as ${voice.possessive} principal residence when a trust or company owns it only if ${voice.pronoun} have reasonable security of tenure (for example no real risk of being asked to leave).`} />
                                        </span>
                                    }
                                    name="securityOfTenure"
                                    value={toYesNo(home.securityOfTenure ?? true)}
                                    onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                    options={[
                                        { label: 'Yes', value: 'yes' },
                                        { label: 'No / not sure', value: 'no' },
                                    ]}
                                />
                            </div>
                        </>
                    )}
                </div>
            </fieldset>

            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">If the home were rented out</legend>
                {/* Said plainly because the screen previously read as a contradiction: these fields ask
                    what the home would rent for, while a partner is living in it. Renting it out means
                    they are not — which is also how the engine treats it, since letting the home ends
                    the means-test exemption. Nothing here commits anyone to renting.

                    The WHY is goal-aware: keeping and renting the former home is a real strategy the
                    suggestion engines compare for residential aged care and retirement village moves,
                    but a downsize-only client will sell — for them this section is noise, so it says so. */}
                <p className="text-sm text-gray-600 mb-4">
                    {(() => {
                        const stepGoals = (factFind?.goals || {}) as Record<string, boolean>;
                        if (stepGoals.retirement_village) {
                            return <>Some people keep their home and rent it out to help fund village living — if you fill these in, we&apos;ll compare that path for you. Nothing here commits {voice.subject} to it.</>;
                        }
                        if (stepGoals.aged_care) {
                            return <>Keeping the home and renting it out is one way to fund residential care — if you fill these in, we&apos;ll compare that path for you. Nothing here commits {voice.subject} to it.</>;
                        }
                        return <>Renting the home out isn&apos;t usually part of a downsizing plan (you&apos;re selling) — leave these blank unless it&apos;s a path you want modelled. Nothing here commits {voice.subject} to it.</>;
                    })()}
                    {partnerStayingHome
                        ? ` Renting it out would mean ${partnerName} living somewhere else, so we model that path separately from ${partnerName} staying put.`
                        : ''}
                </p>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <CurrencyInput
                        label={<span>Likely weekly rent if leased? ($) <InfoIcon text={STEP4_FIELD_TOOLTIPS.likelyRent} /></span>}
                        name="likelyRent"
                        value={home.likelyRent || ''}
                        onChange={(v) => updateFactFind('likelyRent', v)}
                    />
                    <div>
                        <label htmlFor="likelyExpenses" className="block text-sm font-medium text-gray-700 mb-1">
                            <span className="inline-flex items-center flex-wrap gap-0">
                                Estimated annual expenses if rented? ($)
                                <InfoIcon text={STEP4_FIELD_TOOLTIPS.likelyExpenses} />
                            </span>
                        </label>
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <CurrencyInput label="Expenses ($)" name="likelyExpenses" value={home.likelyExpenses || ''} onChange={(v) => updateFactFind('likelyExpenses', v)} className="[&>label]:hidden" />
                            </div>
                            <button 
                                type="button"
                                onClick={() => setShowRentalCalculator(true)} 
                                className="flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-800 font-semibold rounded-lg hover:bg-teal-200 transition-colors whitespace-nowrap"
                            >
                                <Calculator size={20} /> Calculate
                            </button>
                        </div>
                    </div>
                </div>
            </fieldset>

            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    About {voice.possessive} property
                    <InfoIcon text={`Understanding ${voice.possessive} property characteristics has implications to factors such as age pension eligibility as well as what's included to assess factors such as aged care costs.`} />
                </legend>
                <div className="space-y-6">
                    <div>
                        <FormRadioGroup label="Is the home on land larger than 2 hectares (5 acres)?" name="landSize" value={toYesNo(home.landSize)} onChange={(n, v) => updateFactFind(n, fromYesNo(v))} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                        {home.landSize && (
                            <div className="pl-6 mt-4 space-y-4 border-l-2 border-blue-200">
                                <CurrencyInput
                                    label={<span>Roughly what is the land beyond 2 hectares worth? ($) <InfoIcon text={`Only the home and the first 2 hectares around it are exempt from the Age Pension assets test. Give the value of the land past that point — a rough figure is fine. Include it in the home value above as well; we do not count it twice.`} /></span>}
                                    name="landSizeValue"
                                    value={home.landSizeValue || ''}
                                    onChange={(v) => updateFactFind('landSizeValue', v)}
                                />
                                {/* The two limbs of the extended land use test. Asked here rather than only
                                    under "primary production", because the test is not limited to farms —
                                    a long-held rural block qualifies on the same terms. A farming client is
                                    not asked twice: their farm answers already satisfy both (homeLandRules.ts). */}
                                {!home.primaryProductionUse && (
                                    <>
                                        <FormRadioGroup
                                            label={<span>{voice.verb('Have', 'Has')} {voice.subject} lived on this property for 20 years or more? <InfoIcon text="Centrelink can exempt ALL the land around the home — however big — for someone with a long-term connection to the property. Twenty years of living there continuously is the test. Short absences, such as a stay in hospital or respite care, do not break it." /></span>}
                                            name="longTermLandAttachment"
                                            value={toYesNo(home.longTermLandAttachment)}
                                            onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                            options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                                        />
                                        <FormRadioGroup
                                            label={<span>Is that extra land being put to use? <InfoIcon text="The second half of the same Centrelink test. It is met if the land earns something — grazing, cropping, leased to a neighbour — or if it realistically cannot earn anything (too steep, bushland, no water). It is not met if the land could be earning and simply sits idle." /></span>}
                                            name="effectiveLandUse"
                                            value={toYesNo(home.effectiveLandUse)}
                                            onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                            options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <FormRadioGroup label="Is the home and land held on two separate titles?" name="separateTitles" value={toYesNo(home.separateTitles)} onChange={(n, v) => updateFactFind(n, fromYesNo(v))} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                        {home.separateTitles && <CurrencyInput className="mt-2" label={<span>What is the land on the second title worth? ($) <InfoIcon text={`A title without the house on it is not part of the home, so Centrelink counts it as an ordinary asset whatever its size — there is no exemption for it. Include it in the home value above as well; we do not count it twice.`} /></span>} name="separateTitlesValue" value={home.separateTitlesValue || ''} onChange={(v) => updateFactFind('separateTitlesValue', v)} />}
                    </div>
                    <div>
                        <FormRadioGroup label="Is the property used for primary production (e.g., a farm)?" name="primaryProductionUse" value={toYesNo(home.primaryProductionUse)} onChange={(n, v) => updateFactFind(n, fromYesNo(v))} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                        {home.primaryProductionUse && (
                             <div className="pl-6 mt-4 space-y-4 border-l-2 border-blue-200">
                                <FormRadioGroup
                                    label={<span>{voice.isThirdPerson ? `${voice.verb('Have', 'Has')} ${voice.subject} continuously worked the farm for the last 20 years?` : 'Have you or your partner continuously worked the farm for the last 20 years?'} <InfoIcon text="This helps establish a 'long-term continuous link' to the land, which is a key part of qualifying for the primary production concession." /></span>}
                                    name="farmContinuouslyWorked"
                                    value={toYesNo(home.farmContinuouslyWorked)}
                                    onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                    options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                                />
                                <FormRadioGroup
                                    label={<span>Is the farm {voice.possessive} primary source of income? <InfoIcon text="This helps determine if the farming activity is substantial, not just a hobby, which is another key part of the concession test." /></span>}
                                    name="farmPrimaryIncome"
                                    value={toYesNo(home.farmPrimaryIncome)}
                                    onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                    options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                                />
                            </div>
                        )}
                    </div>
                    {!home.primaryProductionUse && (
                        <div>
                            <FormRadioGroup
                                label={<span>Is a general business (non-farming) run from the property?</span>}
                                name="businessUse"
                                value={toYesNo(home.businessUse)}
                                onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                            />
                            {home.businessUse && (
                                <div className="pl-6 mt-4 space-y-4 border-l-2 border-blue-200">
                                     <FormRadioGroup
                                        label={voice.isThirdPerson ? `Is the business expected to continue if ${voice.subject} ${voice.verb('move', 'moves')} into care?` : 'Is the business expected to continue if you (or your partner) move into care?'}
                                        name="businessContinues"
                                        value={toYesNo(home.businessContinues)}
                                        onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                        options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                                    />
                                    {home.businessContinues && (
                                        <>
                                            <FormRadioGroup
                                                label={<span>Who is associated with (runs) the business?</span>}
                                                name="businessAssociation"
                                                value={home.businessAssociation || 'self'}
                                                onChange={updateFactFind}
                                                options={[
                                                    {label: isPartnered ? `${client?.firstName || 'Applicant 1'} / ${partner?.firstName || 'Partner'}` : `${client?.firstName || 'Applicant 1'}`, value: 'self'},
                                                    {label: 'Someone else (e.g., child)', value: 'other'}
                                                ]}
                                            />
                                            {home.businessAssociation === 'other' && (
                                                 <FormRadioGroup
                                                    label={<span>{voice.verb('Do', 'Does')} {voice.subject} receive commercial rent or any income from this business? <InfoIcon text={`If ${voice.pronoun} receive income, Centrelink may treat the business portion of the home as an asset. If it's an informal arrangement with no rent paid, it's generally not assessed.`} /></span>}
                                                    name="businessReceivesRent"
                                                    value={toYesNo(home.businessReceivesRent)}
                                                    onChange={(n, v) => updateFactFind(n, fromYesNo(v))}
                                                    options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]}
                                                />
                                            )}
                                            {(home.businessAssociation === 'self' || home.businessReceivesRent) && (
                                                <CurrencyInput
                                                    label={<span>What is the value of the business portion? ($) <InfoIcon text={`Estimate the value of the part of ${voice.possessive} home used exclusively for the business. E.g., if 20% of the home is a dedicated office, the value would be 20% of the home's total value.`} /></span>}
                                                    name="businessUseValue"
                                                    value={home.businessUseValue || ''}
                                                    onChange={(v) => updateFactFind('businessUseValue', v)}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    <div>
                        {/* The Home Equity Access Scheme question used to sit here, alongside the
                            reverse-mortgage one, and its answer went nowhere either: the balance was
                            stored on the home and read by no calculation. Both are now liabilities
                            (Step 6), so they reach net worth, the balance sheet and the projections. */}
                    </div>
                     <div>
                        {/* The reverse-mortgage question used to sit here, and its answer went nowhere:
                            the balance was stored on the home and read by no calculation at all. It is
                            now a liability like any other (Step 6), so it reaches net worth, the
                            balance sheet and the projections. Owner's call, 2026-08-22: one place for
                            every debt. */}
                    </div>
                </div>
            </fieldset>

            {/* Only asked when it IS a question — twice over. A partner staying in the home answers
                it already (that conclusion is stated in the Step 4 insight panel instead). And the
                protected-person answer only affects the AGED CARE means test, so a client who never
                selected residential care as a goal was being asked "once you've both moved into aged
                care..." about a move they aren't making. If the goal is added later, the question
                appears then; until it does, the field's default ('no') is simply unused. */}
            {!partnerStayingHome && Boolean((factFind?.goals as Record<string, boolean> | undefined)?.aged_care) && (
                <fieldset>
                    <legend className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                        Who will stay in {voice.possessive} home?
                        <InfoIcon text={`Certain people are treated as a 'protected person' by Centrelink — typically a partner, a dependent child, or a long-term carer or close relative who meets certain criteria. If a protected person keeps living in ${voice.possessive} home after ${voice.subject} ${voice.verb('move', 'moves')} to aged care, the value of the home is exempt from the aged care means test.`} />
                    </legend>
                    <p className="text-sm text-gray-600 mb-4">
                        {isPartnered
                            ? `Once ${clientName} and ${partnerName} have both moved into aged care, will anyone still be living in their home?`
                            : `If ${voice.subject} ${voice.verb('move', 'moves')} into aged care, will anyone still be living in ${voice.possessive} home?`}{' '}
                        Only certain people make the home exempt from the aged care means test.
                    </p>
                    <FormSelect label="Will someone remain living there?" name="protectedPerson" value={home.protectedPerson || 'no'} onChange={updateFactFind}>
                        <option value="no">No, the home will be empty</option>
                        <option value="dependent_child">Yes, a dependent child</option>
                        <option value="carer">Yes, a carer (who has lived there 2+ years and gets income support)</option>
                        <option value="close_relative">Yes, a close relative (who has lived there 5+ years and gets income support)</option>
                        {/* Someone can genuinely live there without being a protected person — an adult
                            child in work is the common one. Without this there was no true answer for
                            that client: "No" contradicted the question, and picking a protected
                            category to mean "occupied" exempted a home that is actually assessed. */}
                        <option value="occupied_not_protected">Yes, but not one of the above (the home is still assessed)</option>
                        {/* The answer a reader actually reaches for. Asked "who will stay in the home?",
                            a client answered with his PLAN rather than an occupancy category — he was
                            looking for "I will rent it out" and settled for "not one of the above"
                            (client meeting, 2026-08-20). Same means-test treatment as the two answers
                            above it, so his figures were right; the record just did not describe his
                            intention, and he had to guess to get there. */}
                        <option value="will_rent_out">No — the home will be rented out (it is still assessed)</option>
                    </FormSelect>
                </fieldset>
            )}
            
            {/* Rental Expenses Calculator Modal */}
            {showRentalCalculator && (
                <RentalExpensesCalculator 
                    onClose={() => setShowRentalCalculator(false)}
                    onSave={(total) => {
                        updateFactFind('likelyExpenses', total);
                        setShowRentalCalculator(false);
                    }}
                    propertyValue={Number(home.value) || 0}
                    weeklyRent={Number(home.likelyRent) || 0}
                    currentExpenses={Number(home.likelyExpenses) || 0}
                />
            )}
        </div>
    </div>
    );
};

// Thin wrapper over the shared InsightCard so every Step 4 callout uses the Tier-A surface.
// Maps the legacy tone names (blue/amber/green) onto InsightCard tones.
type Step4Tone = 'blue' | 'amber' | 'green';
const STEP4_TONE_MAP: Record<Step4Tone, 'info' | 'caution' | 'positive'> = { blue: 'info', amber: 'caution', green: 'positive' };

interface Step4InsightCalloutProps {
    heading?: React.ReactNode;
    tone?: Step4Tone;
    children: React.ReactNode;
}

const Step4InsightCallout = ({ heading, tone = 'blue', children }: Step4InsightCalloutProps) => (
    <InsightCard tone={STEP4_TONE_MAP[tone] || 'info'} heading={heading} className="mt-5">
        {children}
    </InsightCard>
);

interface GrannyFlatFieldsProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    voice: ApplicantVoice;
}

// Granny-flat fact-find fields — shared by the legacy 'granny_flat' residency and the derived
// "living with family + paid for a right to live there" path.
const GrannyFlatFields = ({ factFind, updateFactFind, voice }: GrannyFlatFieldsProps) => {
    const gf = factFind.grannyFlat || {};
    const hasAmounts = (Number(gf.amountPaid) || 0) > 0 || (Number(gf.propertyTransferredValue) || 0) > 0 || (Number(gf.constructionCost) || 0) > 0;
    const dep = getGrannyFlatDeprivationFromFactFind(factFind as unknown as FactFind, PENSION_RATES.couple);
    return (
    <>
        <p className="text-sm text-gray-500 mb-4">A granny flat arrangement gives {voice.subject} a lifetime right to live there. These details let us check {voice.possessive} homeowner status and whether any of what {voice.pronoun} paid is treated as a gift.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">
            <FormInput label={`Date ${voice.subject} entered the arrangement`} name="dateEntered" type="date" value={factFind.grannyFlat?.dateEntered || ''} onChange={(n,v) => updateFactFind('grannyFlat',n,v)} />
            <CurrencyInput label={`Amount ${voice.subject} paid (cash)`} value={factFind.grannyFlat?.amountPaid ?? ''} onChange={(v) => updateFactFind('grannyFlat','amountPaid',v)} placeholder="$0" />
            <CurrencyInput label={`Value of property or assets ${voice.subject} signed over`} value={factFind.grannyFlat?.propertyTransferredValue ?? ''} onChange={(v) => updateFactFind('grannyFlat','propertyTransferredValue',v)} placeholder="$0" />
            {/* Which property: the home they lived in is taken at face value; another property or
                other assets are tested like cash (owner, 8 Oct 2026). */}
            <FormSelect label={`What did ${voice.subject} sign over?`} name="transferredKind" value={gf.transferredKind || 'home'} onChange={(_n, v) => updateFactFind('grannyFlat', 'transferredKind', v)}>
                <option value="home">The home {voice.pronoun} lived in</option>
                <option value="other_property">Another property (an investment property or holiday home)</option>
                <option value="other_assets">Other assets (shares, savings in kind, or similar)</option>
            </FormSelect>
            <CurrencyInput label="Construction cost (if a flat was built)" value={factFind.grannyFlat?.constructionCost ?? ''} onChange={(v) => updateFactFind('grannyFlat','constructionCost',v)} placeholder="$0" />
        </div>
        <p className="mt-2 text-xs text-gray-500">Cash {voice.pronoun} kept after selling a home is not paid for the right: record it under Assets as it stands today. Leave fields at $0 if they don't apply.</p>
        {hasAmounts && !gf.dateEntered && (
            <Step4InsightCallout>
                Add the <strong>date {voice.subject} entered the arrangement</strong> above and we'll show you whether any of this is treated as a gift.
            </Step4InsightCallout>
        )}
        {/* A gift ages out five years on: the means test windows gifts by financial year (the current
            one and the four before — giftingDeprivation.ts), so one dated earlier than that is already
            ignored by every calculation; say so rather than warn about it (owner, 8 Oct 2026). */}
        {dep && dep.deprivation > 0 && dep.establishmentFyEnd < (new Date().getMonth() >= 6 ? new Date().getFullYear() + 1 : new Date().getFullYear()) - 4 && (
            <Step4InsightCallout heading="A gift arose, but the five years have passed">
                About <strong>{formatCurrency(dep.deprivation)}</strong> was treated as a <strong>gift</strong> when the arrangement started (FY{dep.establishmentFyEnd}) — the amount {voice.pronoun} paid <em>above</em> the assessed value of the right. Gifts count for five years, and that time is up, so it no longer affects {voice.pronounPossessive} pension and nothing from it is carried into the calculations.
            </Step4InsightCallout>
        )}
        {dep && dep.deprivation > 0 && dep.establishmentFyEnd >= (new Date().getMonth() >= 6 ? new Date().getFullYear() + 1 : new Date().getFullYear()) - 4 && (
            <Step4InsightCallout heading="Part of this is treated as a gift" tone="amber">
                Based on what you've entered, about <strong>{formatCurrency(dep.deprivation)}</strong> is treated as a <strong>gift</strong> (in FY{dep.establishmentFyEnd}) — the amount {voice.pronoun} paid <em>above</em> the assessed value of the right. It may reduce {voice.pronounPossessive} pension for five years, and we carry it into the Gifting section automatically (no need to re-enter it).
            </Step4InsightCallout>
        )}
        {dep && dep.deprivation === 0 && hasAmounts && (
            <Step4InsightCallout heading="None of this is treated as a gift" tone="green">
                Transferring {voice.possessive} home or other assets in return for the right to live there is generally accepted as <strong>fair value for fair value</strong> — so it doesn't reduce {voice.pronounPossessive} pension. A gift would only arise if {voice.pronoun} paid <em>more</em> than the assessed value of the right (for example, extra cash on top of a property {voice.pronoun} transferred).
            </Step4InsightCallout>
        )}
    </>
    );
};


/**
 * Where each person lives now — the question the whole of step 4 hangs off.
 *
 * It used to be a dropdown among the name-and-date fields on step 2, which understated it badly.
 * This one answer decides whether the family home is treated as owned (the largest single lever in
 * the aged care means test), feeds household status and the illness-separated rules, is the way in
 * to granny-flat treatment, and swaps every question and insight on this step. The app's own
 * completeness model already counted it as part of Living Arrangements rather than Your Details
 * (profileCompleteness.ts), so the form was the odd one out.
 *
 * Cards rather than a dropdown so all five options are visible without opening anything, and
 * rather than photographs because this repeats per person — a couple would have faced ten images
 * for one factual question. It starts unanswered: nothing about a client's housing should be
 * assumed on their behalf, and everything below waits for the answer.
 */
const RESIDENCY_OPTIONS: { id: string; label: string; description: string; icon: LucideIcon }[] = [
    { id: 'own_home', label: 'Own home', description: 'Owned outright or with a mortgage.', icon: Home },
    { id: 'renting', label: 'Renting', description: 'Renting privately or in public housing.', icon: KeyRound },
    { id: 'with_family', label: 'Living with family', description: "Living in a family member's home.", icon: Users },
    { id: 'retirement_village', label: 'Retirement village', description: 'Already living in a village.', icon: Building2 },
    { id: 'aged_care', label: 'Residential aged care', description: 'Already living in residential care.', icon: HeartPulse },
];

interface ResidencyChoiceProps {
    who: 'client' | 'partner';
    legend: string;
    value: string;
    onChange: (value: string) => void;
}

const ResidencyChoice = ({ who, legend, value, onChange }: ResidencyChoiceProps) => (
    <fieldset>
        <legend className="text-base font-semibold text-gray-800 mb-3">{legend}</legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {RESIDENCY_OPTIONS.map((option) => {
                const isSelected = value === option.id;
                const Icon = option.icon;
                return (
                    <label key={option.id} className="block cursor-pointer">
                        {/* A real radio, visually hidden — keyboard, arrow keys and screen readers
                            all behave natively and the card is simply its label. The same pattern
                            step 1 uses for "who's completing this form". */}
                        <input
                            type="radio"
                            name={`${who}-residency`}
                            value={option.id}
                            checked={isSelected}
                            onChange={() => onChange(option.id)}
                            className="peer sr-only"
                        />
                        <div
                            className={`flex h-full items-start gap-3 rounded-xl border-2 bg-white p-4 transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-[#0290F5] peer-focus-visible:ring-offset-2 ${
                                isSelected
                                    ? 'border-[#0F52E0] shadow-cardlg'
                                    : 'border-slate-200 shadow-card hover:border-slate-300'
                            }`}
                        >
                            <Icon size={20} className={`shrink-0 mt-0.5 ${isSelected ? 'text-[#0F52E0]' : 'text-slate-400'}`} aria-hidden="true" />
                            <span className="min-w-0">
                                <span className={`block text-sm font-semibold ${isSelected ? 'text-[#001E7A]' : 'text-gray-900'}`}>{option.label}</span>
                                <span className="mt-0.5 block text-xs text-gray-600">{option.description}</span>
                            </span>
                            {isSelected && <Check size={18} strokeWidth={3} className="ml-auto shrink-0 text-[#0F52E0]" aria-hidden="true" />}
                        </div>
                    </label>
                );
            })}
        </div>
    </fieldset>
);

interface Step4LivingSituationProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    isPartnered: boolean;
    voice: ApplicantVoice;
}

const Step4_LivingSituation = ({ factFind, updateFactFind, isPartnered, voice }: Step4LivingSituationProps) => (
    <div>
        <StepHeader icon={Home} step={4} title="Living Arrangements" />
        <div className="space-y-8">
            <ResidencyChoice
                who="client"
                legend={voice.isThirdPerson
                    ? `Where does ${factFind.client?.firstName || 'the applicant'} live now?`
                    : 'Where do you live now?'}
                value={String(factFind.client?.currentResidency || '')}
                onChange={(v) => updateFactFind('client', 'currentResidency', v)}
            />
            {isPartnered && (
                <ResidencyChoice
                    who="partner"
                    legend={`Where does ${factFind.partner?.firstName || 'their partner'} live now?`}
                    value={String(factFind.partner?.currentResidency || '')}
                    onChange={(v) => updateFactFind('partner', 'currentResidency', v)}
                />
            )}
        </div>
    </div>
);

interface Step4AltLivingProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    voice: ApplicantVoice;
    /** False when the living-situation chooser above has already rendered the step header. */
    showHeader?: boolean;
}

/**
 * Both of a couple in the same kind of arrangement, where the fact-find holds ONE record for it.
 *
 * Almost always right — a couple share the village unit or the rented flat, and one set of figures
 * describes it. It is wrong for the rare couple in SEPARATE villages, and the honest thing is to
 * say which one is being recorded rather than take the first and drop the other. Holding two is a
 * change to the stored shape and to everything downstream that reads it; backlogged in the
 * methodology register (2026-08-20).
 */
const SharedArrangementNote = ({ what }: { what: string }) => (
    <p className="mb-4 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-sm text-slate-700">
        These figures cover the {what} you share. If you are in separate ones, enter the main
        arrangement here — only one can be recorded at present, so the plan will not reflect the
        cost of the second one.
    </p>
);

const Step4_AltLiving = ({ factFind, updateFactFind, voice, showHeader = true }: Step4AltLivingProps) => {
    const isCouple = factFind.adviceFor === 'couple';
    const clientRes = factFind.client?.currentResidency;
    const partnerRes = isCouple ? factFind.partner?.currentResidency : undefined;

    /**
     * Whose arrangement this is — used to head each section by name when a couple live apart, and
     * null when there is nothing to disambiguate (a single client, or both of them in the same
     * arrangement). "Michael's retirement village details" beside "The Family Home" is the whole
     * point of showing the two together.
     */
    const whoseArrangement = (residency: string): string | null => {
        if (!isCouple) return null;
        const clientHasIt = clientRes === residency;
        const partnerHasIt = partnerRes === residency;
        if (clientHasIt && partnerHasIt) return null; // shared — naming one of them would mislead
        if (clientHasIt) return factFind.client?.firstName || null;
        if (partnerHasIt) return factFind.partner?.firstName || null;
        return null;
    };
    /** Heads a section with a name when only one of a couple is in that arrangement. */
    const sectionTitle = (residency: string, title: string): string => {
        const who = whoseArrangement(residency);
        return who ? `${who}'s ${title.charAt(0).toLowerCase()}${title.slice(1)}` : title;
    };
    /** True when BOTH are in this arrangement — the fact-find holds one record for it. */
    const sharedArrangement = (residency: string): boolean =>
        isCouple && clientRes === residency && partnerRes === residency;

    /**
     * The voice for ONE section, scoped to whoever is in that arrangement.
     *
     * The sections were headed by name but everything inside them still spoke about the couple:
     * "Tara's retirement village details" over a field labelled "Michael & Tara's entry
     * contribution", for a village only Tara lives in (adviser-reported, 2026-08-20). A section
     * about one person has to talk about that person throughout — the labels are what an adviser
     * reads while typing figures into them.
     *
     * Falls back to the page voice when there is nobody to single out (a single client, or both of
     * them in the same arrangement), and when the section belongs to the READER — someone filling
     * this in about their own rental should still see "you", not their own name.
     */
    const sectionVoice = (residency: string): ApplicantVoice => {
        const who = whoseArrangement(residency);
        if (!who) return voice;
        const belongsToReader = !voice.isThirdPerson && clientRes === residency;
        if (belongsToReader) return voice;
        return {
            ...voice,
            isThirdPerson: true,
            subject: who,
            Subject: who,
            possessive: `${who}'s`,
            Possessive: `${who}'s`,
            pronoun: 'they',
            pronounPossessive: 'their',
            verb: (_plural: string, singular: string) => singular,
        };
    };
    const vRent = sectionVoice('renting');
    const vVillage = sectionVoice('retirement_village');
    const vGranny = sectionVoice('granny_flat');
    const vFamily = sectionVoice('with_family');

    const isRenting = clientRes === 'renting' || partnerRes === 'renting';
    const inVillage = clientRes === 'retirement_village' || partnerRes === 'retirement_village';
    const inGrannyFlat = clientRes === 'granny_flat' || partnerRes === 'granny_flat';
    const withFamily = clientRes === 'with_family' || partnerRes === 'with_family';
    const alreadyInCare = clientRes === 'aged_care' || partnerRes === 'aged_care';
    const inCareNames = [
        clientRes === 'aged_care' ? factFind.client?.firstName : null,
        isCouple && partnerRes === 'aged_care' ? factFind.partner?.firstName : null,
    ].filter(Boolean) as string[];
    const stayingHomeName = clientRes === 'own_home'
        ? factFind.client?.firstName
        : (isCouple && partnerRes === 'own_home' ? factFind.partner?.firstName : null);
    const grannyFlatInterest = factFind.livingWithFamily?.grannyFlatInterest; // undefined | true | false
    return (
    <div>
        {/* No wrapper heading when the chooser above already carries the step header: each section
            below is titled by the person it belongs to, and "Michael & Tara's Living Situation"
            stacked over "Tara's retirement village details" read as two headings for one thing. A
            rule keeps the separation from the family-home section above. */}
        {showHeader
            ? <StepHeader icon={Home} step={4} title={`${voice.Possessive} Living Situation`} />
            : <hr className="mt-8 mb-6 border-slate-200" />}
        {holdsHomeNobodyLivesIn(factFind as unknown as FactFind) && (
            /* A home value with nobody living in it: still owned (then it counts as an asset), or
               left behind when the arrangement changed. The profile asks rather than guessing
               (owner's living-arrangement review, 7 Oct 2026). */
            <div className="mb-6 rounded-lg border border-slate-200 border-l-4 border-l-amber-500 bg-white px-4 py-3 text-sm text-slate-700">
                <p>
                    {voice.Possessive} profile still holds a home worth <strong>{formatCurrency(Number(factFind.home?.value) || 0)}</strong>, but
                    nobody is recorded as living in it. If {voice.subject} still {voice.verb('own', 'owns')} it, leave it: it counts as an
                    asset for the Age Pension. If it has been sold, remove it so the figures are right.
                </p>
                <button type="button" onClick={() => updateFactFind('home', 'value', 0)} className="mt-2 text-sm font-semibold text-blue-700 hover:text-blue-900">
                    The home has been sold — remove it
                </button>
            </div>
        )}
        {alreadyInCare && (['client', 'partner'] as const).filter((who) => (who === 'client' ? clientRes : partnerRes) === 'aged_care').map((who) => {
            const p = who === 'client' ? factFind.client : factFind.partner;
            const stay = (p?.careStay || {}) as { entryDate?: string; facilityName?: string; roomPrice?: number | string; radPaid?: number | string; dailyPayment?: number | string; planningToMove?: boolean; contributionsPaidThisYear?: number | string; contributionsPaidToDate?: number | string };
            const wantsResidentialCare = !!factFind.goals?.aged_care;
            const name = p?.firstName || (who === 'client' ? 'the applicant' : 'their partner');
            const own = !voice.isThirdPerson && who === 'client';
            const set = (field: string, value: unknown) => updateFactFind(who, 'careStay', { ...stay, [field]: value });
            return (
                <fieldset key={who} className="mb-6">
                    <legend className="text-lg font-semibold text-gray-700 mb-1">{own ? 'Your residential aged care' : `${name} — already in residential aged care`}</legend>
                    {/* The stay's own facts (FactFindCareStay). The aged care module reads them to price a
                        change of home and to carry the deposit already paid into any plan; the fee rules
                        follow from the entry date. Until 7 Oct 2026 this section asked nothing and said the
                        module would, which it did not. */}
                    <p className="text-sm text-gray-500 mb-4">
                        A few details about the home {own ? 'you are' : `${name} is`} in now. They let us price a move to another home
                        properly, carry the deposit already paid into any plan, and apply the right fee rules.
                        {stayingHomeName ? ` Because ${stayingHomeName} still lives in the family home, it stays exempt from the aged care means test.` : ''}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                        <FormInput label="Name of the aged care home" name="facilityName" value={stay.facilityName || ''} onChange={(_n, v) => set('facilityName', v)} />
                        <FormInput label={<span>Date {own ? 'you' : name} moved in <InfoIcon text="Moving in before 1 November 2025 keeps the earlier fee rules. The date also tells us how much of the deposit's retention has already been taken." /></span>} name="entryDate" type="date" value={stay.entryDate || ''} onChange={(_n, v) => set('entryDate', v)} />
                        <CurrencyInput label={<span>Agreed room price ($) <InfoIcon text="The price of the room as agreed with the home, whether it is being paid as a deposit, a daily payment or both." /></span>} name="roomPrice" value={stay.roomPrice ?? ''} onChange={(v) => set('roomPrice', v)} placeholder="$0" />
                        <CurrencyInput label={<span>Refundable deposit paid ($) <InfoIcon text="The lump sum paid for the room, if any. It comes back when care ends, less the home's retention, and does not count for the Age Pension." /></span>} name="radPaid" value={stay.radPaid ?? ''} onChange={(v) => set('radPaid', v)} placeholder="$0" />
                        <CurrencyInput label={<span>Daily payment for the room ($ a day) <InfoIcon text="What is paid each day on the part of the room price not paid as a deposit, if any. Leave it blank if the whole room was paid as a deposit." /></span>} name="dailyPayment" value={stay.dailyPayment ?? ''} onChange={(v) => set('dailyPayment', v)} placeholder="$0" />
                        <CurrencyInput label={<span>Means-tested care fees paid so far this year ($) <InfoIcon text="The means-tested care fee, or the non-clinical care and hotelling contributions, paid in the current home since the start of this year. These count towards the yearly cap, so the figures start from where they really are rather than from zero. Leave blank if none, or if the home does not charge them." /></span>} name="contributionsPaidThisYear" value={stay.contributionsPaidThisYear ?? ''} onChange={(v) => set('contributionsPaidThisYear', v)} placeholder="$0" />
                        <CurrencyInput label={<span>Means-tested care fees paid in all since entering care ($) <InfoIcon text="The same fees, added up over every year in residential care. They count towards the lifetime cap, after which no more are charged." /></span>} name="contributionsPaidToDate" value={stay.contributionsPaidToDate ?? ''} onChange={(v) => set('contributionsPaidToDate', v)} placeholder="$0" />
                        {/* Asked only where the goal is residential care: the goals step comes first, so this
                            is the one extra question the arrangement needs (owner, 8 Oct 2026). */}
                        {wantsResidentialCare && (
                            <FormSelect label={<span>{own ? 'Are you' : `Is ${name}`} looking to move to a different aged care home? <InfoIcon text="Moving between homes is common. On a move the deposit paid comes back within 14 days (less any retention taken so far) and can go towards the new home's room, which has its own price and its own fees. The Aged Care module compares staying put with the move." /></span>} name="planningToMove" value={stay.planningToMove === true ? 'yes' : stay.planningToMove === false ? 'no' : ''} onChange={(_n, v) => set('planningToMove', v === 'yes' ? true : v === 'no' ? false : undefined)}>
                                <option value="">Not sure yet</option>
                                <option value="yes">Yes — looking to move</option>
                                <option value="no">No — staying where {own ? 'I am' : `${name} is`}</option>
                            </FormSelect>
                        )}
                    </div>
                </fieldset>
            );
        })}
        {isRenting && (
            <fieldset className="mb-6">
                <legend className="text-lg font-semibold text-gray-700 mb-1">{sectionTitle('renting', 'Renting details')}</legend>
                {sharedArrangement('renting') && <SharedArrangementNote what="rental" />}
                <p className="text-sm text-gray-500 mb-4">A few details about {vRent.possessive} rent let us check {vRent.pronounPossessive} Rent Assistance and asset limits.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <CurrencyInput label={`How much rent ${vRent.verb('do', 'does')} ${vRent.subject} pay per week?`} value={factFind.rent?.rentAmount ?? ''} onChange={(v) => updateFactFind('rent','rentAmount',v)} placeholder="$0" />
                    <FormSelect label={`Who ${vRent.verb('do', 'does')} ${vRent.subject} rent from?`} name="landlordType" value={factFind.rent?.landlordType || 'private'} onChange={(n,v) => updateFactFind('rent',n,v)}>
                        <option value="private">A private landlord or real estate agent</option>
                        <option value="family">A family member {vRent.pronoun} live with</option>
                        <option value="public_housing">Public or community housing</option>
                    </FormSelect>
                    {factFind.adviceFor !== 'couple' && (
                        <FormSelect label={`${vRent.verb('Do', 'Does')} ${vRent.subject} share with others (not a partner)?`} name="sharedAccommodation" value={toYesNo(factFind.rent?.sharedAccommodation)} onChange={(n,v) => updateFactFind('rent',n,fromYesNo(v))}>
                            <option value="no">No</option>
                            <option value="yes">Yes, shared private accommodation</option>
                        </FormSelect>
                    )}
                </div>
            </fieldset>
        )}
        {inVillage && (
            <fieldset className="mb-6">
                <legend className="text-lg font-semibold text-gray-700 mb-1">{sectionTitle('retirement_village', 'Retirement village details')}</legend>
                <p className="text-sm text-gray-500 mb-4">How Centrelink assesses {vVillage.subject} depends on {vVillage.pronounPossessive} entry contribution.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <CurrencyInput label={`${vVillage.Possessive} entry contribution (amount paid to move in)`} value={factFind.retirementVillage?.entryContribution ?? ''} onChange={(v) => updateFactFind('retirementVillage','entryContribution',v)} placeholder="$0" />
                    <CurrencyInput label={`Ongoing fees ${vVillage.subject} ${vVillage.verb('pay', 'pays')} per week`} value={factFind.retirementVillage?.ongoingFees ?? ''} onChange={(v) => updateFactFind('retirementVillage','ongoingFees',v)} placeholder="$0" />
                </div>
                {/* Asked only where the goal is residential care: the goals step comes first, so these are
                    the questions leaving the village needs (owner, 8 Oct 2026). The exit entitlement is
                    worked out from them (villageExit.ts) — the village's departure fee, reinstatement
                    and share of any gain — and the state sets how long the village has to pay it. */}
                {(factFind.goals?.aged_care || factFind.goals?.retirement_village || factFind.goals?.granny_flat) && (() => {
                    const careGoal = !!factFind.goals?.aged_care;
                    const movingTo = careGoal ? 'care' : factFind.goals?.retirement_village ? 'village' : 'granny_flat';
                    const v = (factFind.retirementVillage || {}) as { dateEntered?: string; yearsResidency?: number | string; state?: string; onEnteringCare?: string; dmfPctPerYear?: number | string; dmfCapYears?: number | string; dmfBasis?: string; capitalGainSharePct?: number | string; expectedResaleValue?: number | string; reinstatementCost?: number | string; refundMonths?: number | string; expectedResaleMonths?: number | string };
                    const set = (field: string, value: unknown) => updateFactFind('retirementVillage', field, value);
                    const stateMonths = VILLAGE_REFUND_MONTHS_BY_STATE[String(v.state || '').toUpperCase()];
                    // A couple where only one of them is entering care: the unit's fate is a question —
                    // and the exit terms matter only if it is given up. Both entering, or a single
                    // resident: the unit is given up, no question (owner, 8 Oct 2026).
                    const oneOfCoupleEntering = careGoal && isCouple && factFind.goals?.aged_care_for !== 'both';
                    // The exit terms are asked whichever way the unit goes (8 Oct 2026): a kept unit is in
                    // the family's estate at what the village would pay back, so the estate needs them too.
                    const enteringName = oneOfCoupleEntering
                        ? ((factFind.goals?.aged_care_for === 'partner' ? factFind.partner?.firstName : factFind.client?.firstName) || 'the person entering care')
                        : vVillage.subject;
                    const stayingName = oneOfCoupleEntering
                        ? ((factFind.goals?.aged_care_for === 'partner' ? factFind.client?.firstName : factFind.partner?.firstName) || 'the other')
                        : '';
                    return (
                        <div className="mt-6" data-testid="village-exit-terms">
                            <p className="text-sm font-semibold text-gray-700 mb-1">{movingTo === 'care' ? 'Leaving the village for residential care' : movingTo === 'village' ? 'Leaving the village for another village' : 'Leaving the village for a granny flat'}</p>
                            <p className="text-sm text-gray-500 mb-4">
                                {movingTo !== 'care'
                                    ? <>When {vVillage.subject} {vVillage.verb('move', 'moves')} on, the village pays back the entry contribution, less its departure fee and charges (there may be a waiting period to receive the funds). The {movingTo === 'village' ? 'new village' : 'granny flat'} is paid for from savings at the move, and the payout goes back into savings when it arrives. The contract's terms tell us what comes back and when. Leave what you don't know blank: we then take the full entry contribution as coming back, with no departure fee.</>
                                    : oneOfCoupleEntering
                                    ? <>Only {enteringName} is moving into an aged care home, so the first question is what happens to the unit. If {stayingName} stays on, the unit is kept and stays in the family's estate at what the village would pay back for it. If the unit is given up, the village pays back the entry contribution, less its departure fee and charges (there may be a waiting period to receive the funds). Either way the contract's terms tell us what that is worth. Leave what you don't know blank: we then take the full entry contribution as coming back, with no departure fee.</>
                                    : <>When {vVillage.subject} {vVillage.verb('move', 'moves')} into an aged care home the village pays back the entry contribution, less its departure fee and charges (there may be a waiting period to receive the funds). The contract's terms tell us what comes back and when. Leave what you don't know blank: we then take the full entry contribution as coming back, with no departure fee.</>}
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                {oneOfCoupleEntering && (
                                    <FormSelect label={`When ${enteringName} moves into care, what happens to the unit?`} name="onEnteringCare" value={v.onEnteringCare || ''} onChange={(_n, val) => set('onEnteringCare', val || undefined)}>
                                        <option value="">Select...</option>
                                        <option value="keep_partner_stays">{stayingName} stays on in the unit</option>
                                        <option value="sell">The unit is given up and the village pays out</option>
                                        <option value="sell_when_partner_leaves">Kept for now; given up when {stayingName} leaves too</option>
                                    </FormSelect>
                                )}
                                <FormInput label={<span>Date {vVillage.subject} moved in <InfoIcon text="The departure fee builds up for each year in the village, usually up to a cap. The date sets how many years count." /></span>} name="dateEntered" type="date" value={v.dateEntered || ''} onChange={(_n, val) => set('dateEntered', val)} />
                                <NumberInput label={<span>Years in the village <InfoIcon text="Only if the date moved in is not known." /></span>} name="yearsResidency" value={v.yearsResidency ?? ''} onChange={(val) => set('yearsResidency', val)} />
                                <FormSelect label={<span>State or territory <InfoIcon text="Each state sets how long a village has to pay the exit entitlement where the unit has not resold sooner: about 6 months in Victoria, 12 in NSW and WA, 18 in Queensland and South Australia. Tasmania, the ACT and the NT set no limit, so 12 months is assumed unless the contract says otherwise." /></span>} name="state" value={v.state || ''} onChange={(_n, val) => set('state', val)}>
                                    <option value="">Select...</option>
                                    {['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'ACT', 'NT'].map((st) => <option key={st} value={st}>{st}</option>)}
                                </FormSelect>
                                <NumberInput label={<span>Months the village has to pay <InfoIcon text={`Only if the contract names a time limit. ${stateMonths ? `Leave blank to use the state's limit of ${stateMonths} months.` : "Leave blank to use the state's limit."}`} /></span>} name="refundMonths" value={v.refundMonths ?? ''} onChange={(val) => set('refundMonths', val)} />
                                <NumberInput label={<span>Months until the unit is likely to resell <InfoIcon text="Only if sooner than the limit: the village pays within days of a sale, so the money arrives earlier and its fees after leaving stop at the sale. Leave blank to assume the limit." /></span>} name="expectedResaleMonths" value={v.expectedResaleMonths ?? ''} onChange={(val) => set('expectedResaleMonths', val)} />
                                <PercentageInput label={<span>Departure fee (% a year) <InfoIcon text="Also called the deferred management fee or exit fee. Most contracts charge a percentage for each year of residency, up to a cap: for example 3% a year for up to 10 years." /></span>} name="dmfPctPerYear" value={v.dmfPctPerYear ?? ''} onChange={(val) => set('dmfPctPerYear', val)} />
                                <NumberInput label={<span>Charged for up to (years) <InfoIcon text="The cap on the years the departure fee builds up over: 10 in the example above." /></span>} name="dmfCapYears" value={v.dmfCapYears ?? ''} onChange={(val) => set('dmfCapYears', val)} />
                                <FormSelect label={<span>Departure fee is a % of <InfoIcon text="Contracts charge the fee on the price paid to move in, or on the price the unit sells for. The contract says which." /></span>} name="dmfBasis" value={v.dmfBasis || 'entry'} onChange={(_n, val) => set('dmfBasis', val)}>
                                    <option value="entry">The entry contribution (price paid to move in)</option>
                                    <option value="resale">The resale price</option>
                                </FormSelect>
                                <PercentageInput label={<span>Share of any resale gain kept (%) <InfoIcon text="Where the unit sells for more than was paid, the contract says how the gain is split with the village. Leave blank for an even split." /></span>} name="capitalGainSharePct" value={v.capitalGainSharePct ?? ''} onChange={(val) => set('capitalGainSharePct', val)} />
                                <CurrencyInput label={<span>Expected resale price ($) <InfoIcon text="Leave blank to assume it sells for the entry contribution. A lower figure reduces what comes back; a higher one adds the share of the gain." /></span>} name="expectedResaleValue" value={v.expectedResaleValue ?? ''} onChange={(val) => set('expectedResaleValue', val)} placeholder="$0" />
                                <CurrencyInput label={<span>Reinstatement cost ($) <InfoIcon text="What the contract charges to bring the unit back to resale condition, if anything." /></span>} name="reinstatementCost" value={v.reinstatementCost ?? ''} onChange={(val) => set('reinstatementCost', val)} placeholder="$0" />
                            </div>
                            <Step4InsightCallout heading="What this means">
                                {movingTo !== 'care'
                                    ? <>The {movingTo === 'village' ? 'Retirement Village' : 'Granny Flat'} module pays the current unit out on these terms when the move happens: the money arrives within the state's time limit (or the contract's), goes back into savings, and the current unit's fees stop. Until it arrives, the unit sits in the family's estate at what the village would pay back for it.</>
                                    : oneOfCoupleEntering && !v.onEnteringCare
                                    ? <>Until this is answered we assume {stayingName} stays on: the village fees continue, nothing is paid out, {enteringName} is still treated as a homeowner through the unit, and the unit sits in the family's estate at what the village would pay back for it.</>
                                    : oneOfCoupleEntering && v.onEnteringCare === 'keep_partner_stays'
                                        ? <>{stayingName} stays on in the unit: the village fees continue, nothing is paid out, and {enteringName} is still treated as a homeowner through it. The Aged Care module prices care on that basis, and the unit sits in the family's estate at what the village would pay back for it.</>
                                        : oneOfCoupleEntering && v.onEnteringCare === 'sell_when_partner_leaves'
                                            ? <>The unit is kept while {stayingName} is there, so for now nothing is paid out and {enteringName} is still a homeowner through it. The later sale is not dated, so the Aged Care module prices care on the unit being kept, with the unit in the family's estate at what the village would pay back for it.</>
                                            : <>The Aged Care module works out the exit entitlement from these terms and models the wait for it: until the village pays, the entry contribution still counts as {vVillage.pronounPossessive} home, and any part of the aged care room it is to pay for is charged daily in the meantime. From the day it is paid {vVillage.subject} {vVillage.verb('are', 'is')} assessed as a non-homeowner, and the money can go to the room's deposit or stay in the bank.</>}
                            </Step4InsightCallout>
                        </div>
                    );
                })()}
            </fieldset>
        )}
        {inGrannyFlat && (
            <fieldset className="mb-6">
                <legend className="text-lg font-semibold text-gray-700 mb-1">{sectionTitle('granny_flat', 'Granny flat details')}</legend>
                <GrannyFlatFields factFind={factFind} updateFactFind={updateFactFind} voice={voice} />
            </fieldset>
        )}
        {withFamily && (
            <fieldset className="mb-6">
                <legend className="text-lg font-semibold text-gray-700 mb-1">{sectionTitle('with_family', 'Living with family')}</legend>
                <p className="text-sm text-gray-500 mb-4">First, one question so we treat {vFamily.possessive} situation correctly.</p>
                <FormSelect label={`To live there, did ${vFamily.subject} pay money or transfer ${vFamily.pronounPossessive} home or other assets in return for the right to live there?`} name="grannyFlatInterest" value={toTriYesNo(grannyFlatInterest)} onChange={(n,v) => updateFactFind('livingWithFamily',n,fromTriYesNo(v))}>
                    <option value="">Select...</option>
                    <option value="yes">Yes — {vFamily.isThirdPerson ? 'they' : 'I'} paid money or transferred assets for the right to live there</option>
                    <option value="no">No — {vFamily.isThirdPerson ? 'they' : 'I'} just live with family (and may pay board)</option>
                </FormSelect>
                {grannyFlatInterest === true && (
                    <div className="mt-6"><GrannyFlatFields factFind={factFind} updateFactFind={updateFactFind} voice={voice} /></div>
                )}
                {grannyFlatInterest === false && (
                    <div className="mt-6">
                        {/* The "sold a home" question and its proceeds were removed on 8 Oct 2026: nothing read
                            them, the money is what Assets records today, and a gift of it belongs under Gifting. */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 items-start">
                            <CurrencyInput label={`Board / lodging ${vFamily.subject} ${vFamily.verb('pay', 'pays')} per week`} value={factFind.livingWithFamily?.boardPerWeek ?? ''} onChange={(v) => updateFactFind('livingWithFamily','boardPerWeek',v)} placeholder="$0" />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">If {vFamily.pronoun} sold a home to move in, record what is left of the proceeds under Assets as it stands today. Anything given to family goes under Gifting with its date.</p>
                    </div>
                )}
            </fieldset>
        )}
    </div>
    );
};

type UpdateAsset = (type: AssetType | undefined, id: string | undefined, field: string, value: unknown) => void;

/**
 * ONE control for what a super or pension account is assumed to earn: a risk-profile dropdown whose
 * options carry the preset rates, with a numeric field appearing ONLY when "Custom" is chosen —
 * two always-visible fields describing the same number read as a question ("which one wins?").
 *
 * The dropdown writes ONLY `growthRate` (the field the projections read); the selection is DERIVED
 * by matching the saved rate back to the published table, so the two can never disagree, and a
 * hand-typed 6.6 on a pension honestly shows as Balanced because it IS the Balanced assumption.
 * `explicitCustom` is the one bit of local state: without it, choosing Custom while the rate still
 * equals a preset would immediately derive back to that preset and hide the field mid-edit.
 */
const SuperReturnControl = ({ asset, wrapper, updateAsset, voice }: { asset: DraftAsset; wrapper: SuperWrapper; updateAsset: UpdateAsset; voice: ApplicantVoice }) => {
    const [explicitCustom, setExplicitCustom] = useState(false);
    const derived = profileForSuperRate(wrapper, asset.growthRate);
    const profileId = explicitCustom ? 'custom' : derived;
    return <>
        <FormSelect
            label={<span>Assumed Annual Return (risk profile) <InfoIcon text={`The long-term yearly return assumed for this account — income and growth combined, after fund fees and taxes, which is the figure ${voice.pronounPossessive} fund statement reports. Pick the investment option named on the statement (most funds call these Conservative, Balanced, Growth or similar); Balanced is the most common default. Choose Custom to enter the fund's actual published return instead. Left blank, we assume Balanced.`}/></span>}
            name="investmentProfile"
            value={profileId}
            onChange={(_n, v) => {
                if (v === 'custom') { setExplicitCustom(true); return; }
                setExplicitCustom(false);
                updateAsset(asset.type, asset.id, 'growthRate', superPresetFor(v as InvestmentProfileId, wrapper));
            }}
        >
            {INVESTMENT_PROFILE_IDS.map((id) => (
                <option key={id} value={id}>
                    {`${INVESTMENT_PROFILES[id].label} — ${superPresetFor(id, wrapper).toFixed(2)}% p.a.${id === DEFAULT_INVESTMENT_PROFILE ? ' (most common default)' : ''}`}
                </option>
            ))}
            <option value="custom">Custom — enter my own rate</option>
        </FormSelect>
        {profileId === 'custom' && (
            <PercentageInput
                label={<span>Custom annual return (%) <InfoIcon text="Income and growth combined, after fund fees and taxes — straight off the fund's statement. If it matches one of the standard options, we'll show it under that name."/></span>}
                name="growthRate"
                value={asset.growthRate ?? ''}
                onChange={(v) => updateAsset(asset.type, asset.id, 'growthRate', v)}
            />
        )}
    </>;
};

interface AssetFormFieldsProps {
    asset: DraftAsset;
    updateAsset: UpdateAsset;
    isPartnered: boolean;
    factFind: DraftFactFind;
    voice: ApplicantVoice;
}

// --- AssetFormFields: A dedicated component to render asset-specific fields ---
const AssetFormFields = ({ asset, updateAsset, isPartnered, factFind, voice }: AssetFormFieldsProps) => {
    const assetConfig = ASSET_TYPES.find(a => a.id === asset.type);

    const ownershipOptions = (individualOnly: boolean | undefined) => {
        const options: { value: string; label: string }[] = [{ value: 'client', label: factFind.client?.firstName || 'Applicant 1' }];
        if (isPartnered) {
             options.push({ value: 'partner', label: factFind.partner?.firstName || 'Applicant 2' });
             if(!individualOnly) options.push({ value: 'joint', label: 'Joint' });
        }
        (factFind.structures || []).forEach((structure) => {
            options.push({
                value: entityOwnershipValue(structure.id || ''),
                label: structure.name?.trim() || 'Unnamed arrangement',
            });
        });
        return options;
    };

    // Option C on the CGT data question: don't force the client to supply a cost base or purchase month
    // they may not have, but don't let the fallback assumptions pass unseen either (see cgtDataQuality.ts).
    //
    // Styled as quiet helper text, NOT as a warning: nothing is wrong — the client simply hasn't given us
    // an optional figure, and the estimate still works. A filled amber panel with a warning triangle read
    // as an error on a field the form itself labels "(if known)". Being unobtrusive is also what lets this
    // show on every asset with a gap rather than only large ones, so two assets missing the same field are
    // never treated differently for reasons invisible on screen.
    const renderCgtDataHint = (a: DraftAsset) => {
        const gap = getAssetCgtDataGap(a as unknown as FactFindAsset, a.type || '');
        if (!gap) return null;
        return (
            <p className="md:col-span-2 -mt-1 text-xs text-slate-500 flex items-start gap-1.5">
                <Info size={13} className="shrink-0 mt-0.5 text-slate-400" />
                <span>{describeCgtDataGap(gap)}</span>
            </p>
        );
    };

    const commonFields = (
        <>
            <FormInput label="Description / Institution" name="description" id={`description-${asset.id}`} value={asset.description} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
            <CurrencyInput label={asset.type === 'annuities' ? <span>Current Value ($) <InfoIcon text="The withdrawal value on the provider's statement — what would come back if the annuity were cancelled today (for a fixed term, the capital still to be returned). It is what the estate is worth. Centrelink does not use it for a lifetime annuity: it counts 60% of the purchase price for the first five years, or until age 85 if that is later, and 30% after that." /></span> : 'Current Value ($)'} name="value" id={`value-${asset.id}`} value={asset.value} onChange={(v) => updateAsset(asset.type, asset.id, 'value', v)} />
            <FormSelect label={<span>Ownership {(factFind.structures || []).length > 0 ? <InfoIcon text={`Personal name = in ${voice.pronounPossessive} own name. Choosing a trust or company means the asset is held inside that arrangement.`} /> : null}</span>} name="ownership" id={`ownership-${asset.id}`} value={asset.ownership || 'client'} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)}>
                {ownershipOptions(assetConfig?.individualOnly).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </FormSelect>
        </>
    );

    const superInvestmentFields = (wrapper: SuperWrapper) => (
        <SuperReturnControl asset={asset} wrapper={wrapper} updateAsset={updateAsset} voice={voice} />
    );

    switch (asset.type) {
         case 'investmentProperty':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                {/* Field ORDER is deliberate: this is a 2-column grid and commonFields contributes three
                    cells, so the growth rate takes the odd cell next to Ownership in order to land the two
                    CGT inputs (cost base + acquisition month) side by side on their own row, and rent next
                    to expenses on theirs. Reordering these will silently split those pairs across rows.
                    The CGT pair also matches how Shares lays them out. */}
                <PercentageInput label="Assumed Annual Growth Rate (%)" name="growthRate" value={asset.growthRate || '5'} onChange={(v) => updateAsset(asset.type, asset.id, 'growthRate', v)} />
                {/* One date field, matching Shares. It replaced a separate "Acquisition Date" pre/post-20
                    Sept 1985 dropdown: the two asked for the same fact and could contradict each other, and
                    the exemption is derivable from the date (see capitalGainsTax.ts's isPreCgtAsset). */}
                <CurrencyInput label={<span>Cost Base ($) <InfoIcon text={`What ${voice.subject} originally paid, plus purchase costs such as stamp duty and legal fees. This is subtracted from the sale price to work out the capital gain. If you leave it blank we assume 25% of the current value, which usually overstates the tax.`}/></span>} name="costBase" value={asset.costBase} onChange={(v) => updateAsset(asset.type, asset.id, 'costBase', v)} />
                <FormInput label={<span>Month/Year Acquired (if known) <InfoIcon text={`Roughly when ${voice.subject} bought this — the month is enough. It decides whether the 50% CGT discount applies (which needs more than 12 months of ownership), whether the property is exempt as a pre-20 September 1985 asset, and for sales from 1 July 2027 how the gain is split under the new CGT rules. Leave it blank if you're unsure and we'll assume it has been held more than 12 months; that assumption is disclosed in ${voice.possessive} Statement of Advice if the property is sold.`}/></span>} name="acquiredOn" id={`acquiredOn-${asset.id}`} type="month" value={asset.acquiredOn} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                <CurrencyInput label="Annual Rent ($)" name="rent" value={asset.rent} onChange={(v) => updateAsset(asset.type, asset.id, 'rent', v)} />
                <CurrencyInput label="Annual Expenses ($)" name="expenses" value={asset.expenses} onChange={(v) => updateAsset(asset.type, asset.id, 'expenses', v)} />
                {/* A former home, rented out since the move (owner, 8 Oct 2026): asked only where
                    nobody in the household lives in a home they own. A sale is then priced under the
                    six-year absence rule rather than as an ordinary investment (disposalCgt.ts). */}
                {!anyoneOwnsHome(factFind as unknown as FactFind) && (<>
                    <FormSelect label={<span>Was this the home {voice.subject} lived in before moving? <InfoIcon text={`A home ${voice.subject} moved out of and rented since is sold under the main-residence rules: no CGT within six years of first renting it out, and after that tax only on the time beyond six years, from its value when first rented.`} /></span>} name="formerHome" value={asset.formerHome === true ? 'yes' : asset.formerHome === false ? 'no' : ''} onChange={(_n, v) => updateAsset(asset.type, asset.id, 'formerHome', v === 'yes' ? true : v === 'no' ? false : undefined)}>
                        <option value="">Select...</option>
                        <option value="yes">Yes — {voice.pronoun} lived there and now rent it out</option>
                        <option value="no">No — always an investment</option>
                    </FormSelect>
                    {asset.formerHome === true && (
                        <FormInput label={<span>Month/Year first rented out <InfoIcon text="The six years run from here, and the cost base for any later sale is the property's value at this date." /></span>} name="firstRentedOn" type="month" value={asset.firstRentedOn || ''} onChange={(_n, v) => updateAsset(asset.type, asset.id, 'firstRentedOn', v)} />
                    )}
                </>)}
                {renderCgtDataHint(asset)}
            </div>
        case 'shares':
             return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                <PercentageInput label={<span>Income Rate (% p.a.) <InfoIcon text={`This is the annual income yield (for example dividends/distributions) as a percentage of the current balance. You can usually find it on ${voice.pronounPossessive} annual tax statement, dividend/distribution statement, or ${voice.pronounPossessive} investment platform summary.`}/></span>} name="incomeRate" value={asset.incomeRate} onChange={(v) => updateAsset(asset.type, asset.id, 'incomeRate', v)} />
                <PercentageInput label={<span>Franking Credit (%) <InfoIcon text={`Franking credits represent tax already paid by the company on ${voice.pronounPossessive} behalf. They can reduce the tax ${voice.pronoun} pay on dividends. You can find this rate on ${voice.pronounPossessive} dividend statements, annual tax statements, or ${voice.pronounPossessive} investment platform.`}/></span>} name="frankingCredit" value={asset.frankingCredit || '70'} onChange={(v) => updateAsset(asset.type, asset.id, 'frankingCredit', v)} />
                <PercentageInput label={<span>Assumed Annual Growth Rate (%) <InfoIcon text={`This is ${voice.possessive} long-term capital growth assumption. If unsure, use the provider/app performance history as a guide and keep assumptions conservative for planning.`}/></span>} name="growthRate" value={asset.growthRate || '5'} onChange={(v) => updateAsset(asset.type, asset.id, 'growthRate', v)} />
                <CurrencyInput label={<span>Cost Base ($) <InfoIcon text={`What ${voice.subject} originally paid, plus purchase costs such as brokerage. This is subtracted from the sale price to work out the capital gain. If you leave it blank we assume 25% of the current value, which usually overstates the tax.`}/></span>} name="costBase" value={asset.costBase} onChange={(v) => updateAsset(asset.type, asset.id, 'costBase', v)} />
                <FormInput label={<span>Month/Year Acquired (if known) <InfoIcon text={`Roughly when ${voice.subject} bought these — the month is enough. It decides whether the 50% CGT discount applies (which needs more than 12 months of ownership) and, for sales from 1 July 2027, how the gain is split under the new CGT rules. For a holding built up over time, use when ${voice.pronoun} first started buying. Leave it blank if you're unsure and we'll assume they have been held more than 12 months; that assumption is disclosed in ${voice.possessive} Statement of Advice if the holding is sold.`}/></span>} name="acquiredOn" id={`acquiredOn-${asset.id}`} type="month" value={asset.acquiredOn} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                 <FormRadioGroup label={<span>Reinvest Distributions? <InfoIcon text={`If 'Yes', dividend and distribution payments are automatically used to buy more shares, increasing ${voice.possessive} investment over time. If 'No', payments are received as cash income.`}/></span>} name="reinvest" value={toYesNo(asset.reinvest)} onChange={(n, v) => updateAsset(asset.type, asset.id, n, fromYesNo(v))} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                {renderCgtDataHint(asset)}
            </div>
        case 'accountBasedPensions': { // Use block scope to contain ownerAge
            // Plain derivation (not useMemo) — this runs inside a switch branch, so a hook here
            // would violate the rules of hooks. calculateAge is cheap, so recomputing is fine.
            /*
             * Whose age governs this pension. Derived through the shared helper so the "we cannot
             * tell" case is a real answer rather than a null that silently removes the warning —
             * see components/pensionMinimum.ts.
             */
            const { age: ownerAge, who: ownerName } = abpOwnerAge(asset.ownership, factFind);

            return (
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    {commonFields}
                    <div>
                        <CurrencyInput label={<span>Annual Pension Payment ($) <InfoIcon text={`Usually shown on ${voice.pronounPossessive} account-based pension annual statement or portal. Note: this amount can differ from the legislated minimum for ${voice.pronounPossessive} age due to timing, pro-rata payments in the start year, payment frequency, and ad-hoc withdrawals.`}/></span>} name="income" value={asset.income} onChange={(v) => updateAsset(asset.type, asset.id, 'income', v)} />
                        <PensionMinimumWarning asset={asset} ownerAge={ownerAge} ownerName={ownerName} />
                    </div>
                    {superInvestmentFields('pension')}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                             <span className="flex items-center">
                                When was the pension commenced?
                                <InfoIcon>
                                    <div className="text-left">
                                        <p className="font-bold mb-1">This is crucial for the Centrelink income test:</p>
                                        <p className="mb-1"><strong className="text-blue-300">Post 1 Jan 2015:</strong> The full account balance is 'deemed' to earn a set interest rate.</p>
                                        <p><strong className="text-blue-300">Pre 1 Jan 2015:</strong> A 'deductible amount' (based on life expectancy and original purchase price) is subtracted from {voice.pronounPossessive} annual payment. This is often more favourable. Providing the exact date allows for a more accurate calculation.</p>
                                    </div>
                                </InfoIcon>
                            </span>
                        </label>
                        <div className="flex items-center gap-4">
                             <FormRadioGroup
                                label=""
                                name="commencementDateOption"
                                value={asset.commencementDateOption || 'post-2015'}
                                onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)}
                                options={[{label: 'Pre 1 Jan 2015', value: 'pre-2015'}, {label: 'Post 1 Jan 2015', value: 'post-2015'}]}
                            />
                        </div>
                        {asset.commencementDateOption === 'pre-2015' && (
                            <div className="mt-2 space-y-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormInput label="Exact Commencement Date" name="commencementDate" type="date" value={asset.commencementDate || ''} onChange={(n,v) => updateAsset(asset.type, asset.id, n, v)} disabled={asset.commencementDateUnsure} />
                                    <CurrencyInput label="Original Purchase Price ($)" name="purchasePrice" value={asset.purchasePrice} onChange={(v) => updateAsset(asset.type, asset.id, 'purchasePrice', v)} />
                                </div>
                                <FormCheckbox label="I'm not sure of the exact date" name="commencementDateUnsure" checked={asset.commencementDateUnsure} onChange={(n,v) => updateAsset(asset.type, asset.id, n, v)} />
                                {asset.commencementDateUnsure && <p className="text-xs text-amber-700 bg-amber-100 p-2 rounded-md flex items-center gap-2"><AlertTriangle size={14}/> We will use a worst-case scenario for calculations.</p>}
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        case 'annuities':
             return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    {commonFields}
                    <CurrencyInput label="Annual Payment ($)" name="income" value={asset.income} onChange={(v) => updateAsset(asset.type, asset.id, 'income', v)} />
                     <FormSelect label="Annuity Type" name="annuityType" value={asset.annuityType || ''} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)}>
                        <option value="">Select...</option>
                        <option value="lifetime">Lifetime</option>
                        <option value="fixed_term">Fixed Term</option>
                    </FormSelect>
                    <FormInput label={<span>Date the annuity started (if known) <InfoIcon text="Decides how long it has been held. For a lifetime annuity that sets when Centrelink drops from counting 60% of the purchase price to 30%; for a fixed term it sets how much of the capital has already come back and when the term ends. Leave blank and we count from today." /></span>} name="commencementDate" type="date" value={asset.commencementDate} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                    {asset.annuityType === 'fixed_term' && <NumberInput label="Term (years, if fixed)" name="term" value={asset.term as string | number | undefined} onChange={(v) => updateAsset(asset.type, asset.id, 'term', v)} />}
                    <CurrencyInput label="Purchase Price ($)" name="purchasePrice" value={asset.purchasePrice} onChange={(v) => updateAsset(asset.type, asset.id, 'purchasePrice', v)} />
                    <CurrencyInput label={<span>Residual Capital Value (RCV) ($) <InfoIcon text="The amount of capital left at the end of the annuity term. Enter 0 if none."/></span>} name="rcv" value={asset.rcv} onChange={(v) => updateAsset(asset.type, asset.id, 'rcv', v)} />
                    {asset.annuityType === 'lifetime' && (
                        <>
                            <FormCheckbox label="Payments rise with inflation each year" name="indexed" checked={!!asset.indexed} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                            <NumberInput label={<span>Guarantee period (years, if any) <InfoIcon text="Some lifetime annuities promise a minimum number of years of payments: if death comes within that time, the rest of those payments go to the estate as a lump sum. UniSuper's Lifetime Income has one (the lesser of 10 years and life expectancy); Challenger's plain Liquid Lifetime does not. It is on the provider's statement. Leave blank if there is none." /></span>} name="guaranteePeriodYears" value={asset.guaranteePeriodYears as string | number | undefined} onChange={(v) => updateAsset(asset.type, asset.id, 'guaranteePeriodYears', v)} />
                            {isPartnered && (
                                <PercentageInput label={<span>Share that continues to a spouse on death (%) <InfoIcon text="The percentage of the payment the surviving spouse keeps for life, from the provider's statement — 100% on a joint-life product, 0% where there is no reversion. Stated in the advice; the projection does not model a death." /></span>} name="reversionPct" value={asset.reversionPct} onChange={(v) => updateAsset(asset.type, asset.id, 'reversionPct', v)} />
                            )}
                        </>
                    )}
                    <FormSelect label={<span>Funding Source <InfoIcon text="Annuities bought with super money can have different social security outcomes compared to those bought with personal savings."/></span>} name="fundingSource" value={asset.fundingSource || 'super'} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)}>
                        <option value="super">Superannuation Money</option>
                        <option value="non_super">Personal Savings</option>
                    </FormSelect>
                </div>;
        case 'termDeposits':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                <PercentageInput label="Interest Rate (% p.a.)" name="incomeRate" value={asset.incomeRate} onChange={(v) => updateAsset(asset.type, asset.id, 'incomeRate', v)} />
                <FormInput label="Maturity Date" name="maturityDate" type="date" value={asset.maturityDate} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
            </div>;
        case 'other':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                <CurrencyInput label="Annual Income ($)" name="income" value={asset.income} onChange={(v) => updateAsset(asset.type, asset.id, 'income', v)} />
                <CurrencyInput label="Annual Expenses ($)" name="expenses" value={asset.expenses} onChange={(v) => updateAsset(asset.type, asset.id, 'expenses', v)} />
                <PercentageInput label="Assumed Growth Rate (%)" name="growthRate" value={asset.growthRate} onChange={(v) => updateAsset(asset.type, asset.id, 'growthRate', v)} />
                <CurrencyInput label={<span>Cost Base ($) <InfoIcon text="The original purchase price for CGT calculations. If unknown, we'll assume 25% of current value."/></span>} name="costBase" value={asset.costBase} onChange={(v) => updateAsset(asset.type, asset.id, 'costBase', v)} />
                {/* 'Other Asset' is CGT-relevant (assetTypes.ts marks it hasCgt), so it needs the same
                    acquisition month as shares and investment property — without it the engine always
                    assumed a holding of more than 12 months and granted the 50% discount, with no field
                    to correct it and no hint on screen saying so. */}
                <FormInput label={<span>Month/Year Acquired (if known) <InfoIcon text={`Roughly when ${voice.subject} bought this — the month is enough. It decides whether the 50% CGT discount applies (which needs more than 12 months of ownership), whether it is exempt as a pre-20 September 1985 asset, and for sales from 1 July 2027 how the gain is split under the new CGT rules. Leave it blank if you're unsure and we'll assume it has been held more than 12 months; that assumption is disclosed in ${voice.possessive} Statement of Advice if the asset is sold.`}/></span>} name="acquiredOn" id={`acquiredOn-${asset.id}`} type="month" value={asset.acquiredOn} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                {renderCgtDataHint(asset)}
            </div>;
        case 'bankAccounts':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                <PercentageInput
                    label="Interest Rate (% p.a.)"
                    name="incomeRate"
                    value={asset.incomeRate}
                    onChange={(v) => updateAsset(asset.type, asset.id, 'incomeRate', v)}
                />
            </div>;
        case 'investmentBonds':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                <PercentageInput
                    label={<span>Assumed Growth Rate (% p.a., after the bond&apos;s own tax) <InfoIcon text="An investment bond pays its own tax inside (30% on earnings) and pays nothing out, so this is the growth you would see on the statement. No tax is added on your side. After ten years withdrawals are tax-free; the projection leaves the bond untouched until then." /></span>}
                    name="growthRate"
                    value={asset.growthRate}
                    onChange={(v) => updateAsset(asset.type, asset.id, 'growthRate', v)}
                />
                <FormInput label={<span>Date the bond started (if known) <InfoIcon text={`When ${voice.subject} bought it. Withdrawals become tax-free ten years from this date — the projection will not draw on the bond before then. Leave blank and we count from today.`} /></span>} name="commencementDate" type="date" value={asset.commencementDate} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                <p className="md:col-span-2 -mt-1 text-xs text-slate-500">Centrelink counts an investment bond in full and deems it, like cash or shares.</p>
            </div>;
        /* Owner, 5 Oct 2026: a funeral paid in advance is not a funeral bond, and is fully exempt. */
        case 'prepaidFuneral':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                <p className="md:col-span-2 -mt-1 text-xs text-slate-500">A funeral paid for in advance with a funeral director, under a contract that cannot be refunded. Centrelink does not count it at all, whatever it cost. If it can be refunded, or it is an investment set aside for a funeral, record it as a funeral bond instead.</p>
            </div>;
        /* Owner, 5 Oct 2026: money lent is still an asset — counted at what is owed, and deemed. */
        case 'loansToOthers':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <FormInput label="Who the money was lent to" name="description" id={`description-${asset.id}`} value={asset.description} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                <CurrencyInput label="Still owed ($)" name="value" id={`value-${asset.id}`} value={asset.value} onChange={(v) => updateAsset(asset.type, asset.id, 'value', v)} />
                <FormSelect label="Lent by" name="ownership" id={`ownership-${asset.id}`} value={asset.ownership || 'client'} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)}>
                    {ownershipOptions(assetConfig?.individualOnly).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </FormSelect>
                <CurrencyInput label={<span>Amount first lent ($, optional) <InfoIcon text="Only used to fill in the Services Australia forms, which ask for it. It does not change any figures. Leave it blank and write it on the form yourself if you prefer." /></span>} name="purchasePrice" value={asset.purchasePrice} onChange={(v) => updateAsset(asset.type, asset.id, 'purchasePrice', v)} />
                <FormInput label={<span>Date lent (optional) <InfoIcon text="Only used to fill in the Services Australia forms, which ask for it. It does not change any figures. Leave it blank and write it on the form yourself if you prefer." /></span>} name="commencementDate" type="date" value={asset.commencementDate} onChange={(n, v) => updateAsset(asset.type, asset.id, n, v)} />
                <PercentageInput label="Interest charged (% p.a., if any)" name="incomeRate" value={asset.incomeRate} onChange={(v) => updateAsset(asset.type, asset.id, 'incomeRate', v)} />
                <p className="md:col-span-2 -mt-1 text-xs text-slate-500">Centrelink counts what is still owed and deems it, like money in the bank, whether or not it is being paid back. A loan that has been forgiven is a gift instead.</p>
            </div>;
  case 'personalAssets':
    return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
        {commonFields}
        <PercentageInput
            label="Assumed Growth Rate (%)"
            name="growthRate"
            value={asset.growthRate}
            onChange={(v) => updateAsset(asset.type, asset.id, 'growthRate', v)}
        />
    </div>;
        // Accumulation super previously fell through to the bare default card: no return field at
        // all, while buildNewAsset silently stamped a growth rate the user never saw. The rate now
        // has a visible field and a named investment option, like every other earning asset.
        case 'superannuation':
            return <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                {commonFields}
                {superInvestmentFields('accumulation')}
            </div>;
        default:
            return <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">{commonFields}</div>;
    }
};

interface Step5AssetsProps {
    factFind: DraftFactFind;
    setFactFind: SetFactFind;
    isPartnered: boolean;
    showStructureAdviserFields?: boolean;
    voice: ApplicantVoice;
}

const Step5_Assets = ({ factFind, setFactFind, isPartnered, showStructureAdviserFields = false, voice }: Step5AssetsProps) => {
    const [showImportModal, setShowImportModal] = useState(false);

    const addAsset = (type: AssetType) => {
        const newAsset = buildNewAsset(type, isPartnered);
        const currentAssets = factFind.assets?.[type] || [];
        setFactFind(prev => ({ ...prev, assets: { ...prev.assets, [type]: [...currentAssets, newAsset] } }));

        // Auto-scroll to the newly added asset after a brief delay
        setTimeout(() => {
            const newAssetElement = document.querySelector(`[data-asset-id="${newAsset.id}"]`);
            if (newAssetElement) {
                newAssetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
                // Focus the first input field in the new asset
                const firstInput = newAssetElement.querySelector('input, select, textarea');
                if (firstInput && firstInput instanceof HTMLElement) {
                    firstInput.focus();
                }
            }
        }, 200);
    };

    const updateAsset: UpdateAsset = (type, id, field, value) => {
        if (!type) return;
        const updatedAssets = (factFind.assets?.[type] || []).map(asset => asset.id === id ? { ...asset, [field]: value } : asset);
        setFactFind(prev => ({ ...prev, assets: { ...prev.assets, [type]: updatedAssets } }));
    };

    const removeAsset = (type: AssetType, id: string | undefined) => {
        const filteredAssets = (factFind.assets?.[type] || []).filter(asset => asset.id !== id);
        setFactFind(prev => ({ ...prev, assets: { ...prev.assets, [type]: filteredAssets } }));
    };

    const handleImportConfirm = (picked: { assets: StatementImportDraft[]; liabilities: ImportedLiabilityDraft[] }) => {
        setFactFind(prev => applyStatementImport(prev, picked, isPartnered));
        setShowImportModal(false);
    };

    return (
        <div>
            {showImportModal && (
                <AssetStatementImportModal
                    onClose={() => setShowImportModal(false)}
                    onConfirm={handleImportConfirm}
                    household={{ isCouple: isPartnered, clientName: factFind.client?.firstName || null, partnerName: isPartnered ? (factFind.partner?.firstName || null) : null }}
                    existingAssets={(factFind.assets || {}) as Record<string, unknown>}
                    existingHome={factFind.home || null}
                />
            )}
            <StepHeader icon={Wallet} step={5} title="Assets" />
            <div className="space-y-8">
                {/*
                  * "If a strategy frees up money, how would it be invested?" USED TO SIT HERE, and
                  * was removed on the owner's call (2026-08-28): it introduces a concept that may
                  * never apply, to a client who at this point has no strategy and no figure to
                  * price. The rule was already written down in three places — asked just-in-time,
                  * never a mandatory fact-find step — and this row was the exception to it.
                  *
                  * It also did active harm. Every module's in-context ask is gated on
                  * `!hasAnsweredNewMoneyProfile(factFind)`, so a client who picked something here
                  * to get past the step was never asked again where the prompt names the money it
                  * is about. The vague question switched off the good one.
                  *
                  * The field itself is untouched — saved profiles keep their answer, and the
                  * Suggestion Engine still asks before it ranks. Manual Control, which read the
                  * setting and never offered it, now asks beside the allocation it prices
                  * (components/strategy/NewMoneyProfileField.tsx).
                  */}
                <ClientStructuresSection
                    factFind={factFind}
                    setFactFind={setFactFind}
                    isPartnered={isPartnered}
                    clientName={factFind.client?.firstName || 'Applicant 1'}
                    partnerName={factFind.partner?.firstName || 'Partner'}
                    showAdviserFields={showStructureAdviserFields}
                    voice={voice}
                />

                <fieldset>
                    <legend className="text-lg font-semibold text-gray-700 mb-4">Add Financial & Personal Assets</legend>
                    {(factFind.structures || []).length > 0 && (
                        <p className="text-sm text-gray-600 mb-4">
                            For assets inside a trust or company, choose that arrangement under Ownership and enter the{' '}
                            <strong>full value</strong> of each asset. {voice.Possessive} net worth uses the apportionment percentages on each arrangement.
                        </p>
                    )}
                    <div className="flex gap-4">
                        {/* Choosing the type IS adding it. The select used to arm a separate "Add"
                            button, greyed out until then — and a greyed button reads as decoration
                            rather than as the next step, so a reader picked his account type and
                            waited for something to happen (client meeting, 2026-08-20). An unwanted
                            row is removed with the bin icon that was always there. */}
                        <FormSelect
                            name="assetType"
                            value=""
                            onChange={(name, value) => { if (value) addAsset(value as AssetType); }}
                        >
                             <option value="">Add an asset...</option>
                             {ASSET_TYPES.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                        </FormSelect>
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                        Have a statement?{' '}
                        <button
                            type="button"
                            className="text-blue-600 font-semibold hover:underline focus:outline-none"
                            onClick={() => setShowImportModal(true)}
                        >
                            Import with AI
                        </button>{' '}
                        <span className="text-gray-500">(optional)</span>
                    </p>
                </fieldset>

                {ASSET_TYPES.map(type => (
                    (factFind.assets?.[type.id]?.length ?? 0) > 0 && (
                        <fieldset key={type.id}>
                            <legend className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2"><type.icon className="text-blue-600" /> {type.name}</legend>
                            <div className="space-y-4">
                                {(factFind.assets?.[type.id] || []).map(asset => (
                                    <div key={asset.id} data-asset-id={asset.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-all duration-300">
                                        <div className="flex justify-end">
                                            <button onClick={() => removeAsset(type.id as AssetType, asset.id)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-all duration-300 hover:scale-110"><Trash2 size={20} /></button>
                                        </div>
                                        <AssetFormFields asset={asset} updateAsset={updateAsset} isPartnered={isPartnered} factFind={factFind} voice={voice} />
                                    </div>
                                ))}
                            </div>
                        </fieldset>
                    )
                ))}

                {/* Add Asset Section at Bottom - Only show if there are 2+ assets */}
                {(() => {
                    const totalAssets = ASSET_TYPES.reduce((total, type) => total + (factFind.assets?.[type.id]?.length || 0), 0);
                    return totalAssets >= 2 && (
                        <fieldset className="mt-8 pt-6 border-t-2 border-gray-200">
                            <legend className="text-lg font-semibold text-gray-700 mb-4">Add More Assets</legend>
                            <div className="flex gap-4">
                                {/* Same as the chooser above: picking a type opens its form. */}
                                <FormSelect
                                    name="assetType"
                                    value=""
                                    onChange={(name, value) => { if (value) addAsset(value as AssetType); }}
                                >
                                     <option value="">Add another asset...</option>
                                     {ASSET_TYPES.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                                </FormSelect>
                            </div>
                        </fieldset>
                    );
                })()}
            </div>
        </div>
    );
};
interface Step6LiabilitiesProps {
    factFind: DraftFactFind;
    setFactFind: SetFactFind;
    isPartnered: boolean;
    voice: ApplicantVoice;
}

const Step6_Liabilities = ({ factFind, setFactFind, isPartnered, voice }: Step6LiabilitiesProps) => {
    const liabilityWarnings = useMemo(() => getStructureLiabilityWarnings(factFind), [factFind]);
    const hasStructures = (factFind.structures || []).length > 0;

    const addLiability = (type: string) => {
        const newLiability: DraftLiability = {
            id: newEntityId(),
            type: type,
            description: '',
            frequency: 'monthly',
            assetLink: type === 'mortgages' ? 'home' : 'none',
            ownership: isPartnered ? 'joint' : 'client',
            loanType: 'pi',
            // Only a debt with a term has a repayment to derive. A credit card revolves, so it is
            // recorded as 'manual' from the outset rather than carrying an 'auto' the engine would
            // never act on and the form would never offer a way out of.
            repaymentCalculation: (type === 'mortgages' || type === 'personalLoans') ? 'auto' : 'manual',
            // A capitalising loan always accrues, so it starts with a workable rate rather than a
            // blank the client may not know to fill. The HEAS rate is set by government; the reverse
            // mortgage figure is market-typical and meant to be edited. These match the defaults the
            // engine falls back to (loanPlugin), so the rate shown is the rate modelled.
            ...(type === 'homeEquityAccessScheme' ? { interestRate: 3.95 } : {}),
            ...(type === 'reverseMortgages' ? { interestRate: REVERSE_MORTGAGE_DEFAULT_RATE } : {}),
        };
        const currentLiabilities = factFind.liabilities?.[type] || [];
        setFactFind(prev => ({ ...prev, liabilities: { ...prev.liabilities, [type]: [...currentLiabilities, newLiability] } }));
    };

    const updateLiability = (type: string, id: string | undefined, field: string, value: unknown) => {
        const updatedLiabilities = (factFind.liabilities?.[type] || []).map(item => item.id === id ? { ...item, [field]: value } : item);
        setFactFind(prev => ({ ...prev, liabilities: { ...prev.liabilities, [type]: updatedLiabilities } }));
    };

    const removeLiability = (type: string, id: string | undefined) => {
        const filteredLiabilities = (factFind.liabilities?.[type] || []).filter(item => item.id !== id);
        setFactFind(prev => ({ ...prev, liabilities: { ...prev.liabilities, [type]: filteredLiabilities } }));
    };

    const investmentProperties = factFind.assets?.investmentProperty || [];

    const syncOwnershipFromAssetLink = (type: string, id: string | undefined, assetLink: unknown) => {
        if (type !== 'mortgages' || !assetLink || assetLink === 'home') return;
        const prop = investmentProperties.find((p) => String(p.id) === String(assetLink));
        if (prop?.ownership && isEntityOwnership(prop.ownership)) {
            updateLiability(type, id, 'ownership', prop.ownership);
        }
    };

    /** Any debt row, in any category — what makes the "no debts" confirmation redundant. */
    const hasAnyLiability = Object.values(factFind.liabilities || {}).some((list) => Array.isArray(list) && list.length > 0);

    return (
        <div>
            <StepHeader icon={CreditCard} step={6} title="Liabilities" />
            {hasStructures && (
                <p className="text-sm text-gray-600 mb-4">
                    If a loan secures property held in a trust or company, set <strong>Ownership</strong> to that same
                    arrangement so net worth and pension estimates reflect the debt correctly.
                </p>
            )}
            {liabilityWarnings.length > 0 && (
                <div className="mb-6 space-y-2">
                    {liabilityWarnings.map((msg) => (
                        <div key={msg} className="flex gap-2 p-3 rounded-lg bg-white border border-slate-200 border-l-4 border-l-amber-400 text-sm text-slate-700">
                            <AlertTriangle className="shrink-0" size={18} />
                            <span>{msg}</span>
                        </div>
                    ))}
                </div>
            )}
            <div className="space-y-8">
                <fieldset>
                    <legend className="text-lg font-semibold text-gray-700 mb-4">Add {voice.Possessive} Debts</legend>
                    {/* Choosing the type IS adding it, matching the Assets step. Assets lost their
                        separate Add button after a client read the greyed-out control as decoration
                        and waited; leaving Liabilities on the old pattern just moved the confusion
                        one step down the form. */}
                    <div className="flex gap-4">
                        <FormSelect
                            name="liabilityType"
                            value=""
                            onChange={(name, value) => { if (value) addLiability(value); }}
                        >
                             <option value="">Add a debt...</option>
                             {LIABILITY_TYPES.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                        </FormSelect>
                    </div>
                    {/* Having no debts is a complete answer, but an empty list on its own is
                        indistinguishable from a step nobody filled in — which left finished files
                        showing as incomplete in the adviser's client list. Ticking this says so. */}
                    {!hasAnyLiability && (
                        <label className="mt-4 flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                            <input
                                type="checkbox"
                                name="liabilitiesConfirmedNone"
                                className="mt-0.5"
                                checked={factFind.liabilitiesConfirmedNone === true}
                                onChange={(e) => setFactFind(prev => ({ ...prev, liabilitiesConfirmedNone: e.target.checked }))}
                            />
                            <span>
                                {voice.isThirdPerson
                                    ? `${voice.Subject} ${voice.verb('don’t', 'doesn’t')} have any debts — nothing to add here.`
                                    : 'I don’t have any debts — nothing to add here.'}
                            </span>
                        </label>
                    )}
                </fieldset>

                {LIABILITY_TYPES.map(type => (
                    (factFind.liabilities?.[type.id]?.length ?? 0) > 0 && (
                        <fieldset key={type.id}>
                            <legend className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2"><type.icon className="text-blue-600" /> {type.name}</legend>
                            <div className="space-y-4">
                                {(factFind.liabilities?.[type.id] || []).map(item => {
                                    const calculatedRepayment = calculateLoanRepayment(Number(item.balance) || 0, Number(item.interestRate) || 0, Number(item.loanTerm) || 0, item.frequency || 'monthly', item.loanType || 'pi');
                                    const isMortgage = item.type === 'mortgages';
                                    // A term is what makes a repayment derivable, and a credit card has none —
                                    // it revolves. Mortgages and personal loans amortise, so they get the term,
                                    // the loan type and the auto/manual switch; a card gets a rate and whatever
                                    // the client actually pays each month.
                                    const isTermLoan = isTermLoanType(type.id);
                                    // A reverse mortgage and a HEAS loan are repaid out of the property
                                    // when it is sold, not out of income. Asking for a repayment amount
                                    // invites a figure the client never actually pays, and the engine
                                    // deliberately ignores it (capitalisingLoans.ts) — so the box could
                                    // only ever mislead.
                                    const isCapitalising = isCapitalisingLoan({ type: type.id });
                                    // Asks the ENGINE whether it will derive this repayment, rather than
                                    // deciding separately and drifting from it — which is what produced the
                                    // read-only $0 that swallowed whatever was typed over it.
                                    const autoRepaymentApplies = derivesRepaymentAutomatically(item);

                                    return (
                                    <div key={item.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-all duration-300">
                                        <div className="flex justify-end">
                                            <button onClick={() => removeLiability(type.id, item.id)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full transition-all duration-300 hover:scale-110"><Trash2 size={20} /></button>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                                            <FormInput label="Lender / Description" name="description" id={`liability-description-${item.id}`} value={item.description || ''} onChange={(n, v) => updateLiability(type.id, item.id, n, v)} />
                                            <CurrencyInput label="Outstanding Balance ($)" name="balance" id={`liability-balance-${item.id}`} value={item.balance || ''} onChange={(v) => updateLiability(type.id, item.id, 'balance', v)} />
                                            {hasStructures && (
                                                <FormSelect
                                                    label={
                                                        <span>
                                                            Ownership{' '}
                                                            <InfoIcon text="Who is responsible for this debt? Match a trust or company if the loan secures property inside that arrangement." />
                                                        </span>
                                                    }
                                                    name="ownership"
                                                    id={`liability-ownership-${item.id}`}
                                                    value={item.ownership || (isPartnered ? 'joint' : 'client')}
                                                    onChange={(n, v) => updateLiability(type.id, item.id, n, v)}
                                                >
                                                    {buildOwnershipOptions(factFind, { isPartnered }).map((opt) => (
                                                        <option key={opt.value} value={opt.value}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </FormSelect>
                                            )}
                                            {isMortgage && (
                                                <>
                                                    <FormSelect
                                                        label="Loan secured against"
                                                        name="assetLink"
                                                        id={`liability-assetLink-${item.id}`}
                                                        value={item.assetLink || 'home'}
                                                        onChange={(n, v) => {
                                                            updateLiability(type.id, item.id, n, v);
                                                            syncOwnershipFromAssetLink(type.id, item.id, v);
                                                        }}
                                                    >
                                                        <option value="home">{voice.isThirdPerson ? `${voice.Possessive} Home` : 'Family Home'}</option>
                                                        {investmentProperties.map(prop => <option key={prop.id} value={prop.id}>{prop.description || 'Investment Property'}</option>)}
                                                    </FormSelect>
                                                </>
                                            )}
                                            {/* Asked for EVERY debt now, not just mortgages. The engine already
                                                amortises any liability carrying a rate — accruing interest each
                                                period and taking only the remainder off the principal — so
                                                withholding the question starved a calculation that was ready for
                                                it, and left a 20% card being paid down as though it were free. */}
                                            {/* A capitalising loan shows the rate it is ACTUALLY modelled at
                                                rather than an empty box, because the engine falls back to a
                                                default when none is recorded — a blank field beside a
                                                compounding balance says the interest is nothing. Still fully
                                                editable; the HEAS rate is the government's. */}
                                            <PercentageInput
                                                label={isCapitalising && type.id === 'homeEquityAccessScheme' ? 'Interest Rate (%) — set by government' : 'Interest Rate (%)'}
                                                name="interestRate"
                                                id={`liability-interestRate-${item.id}`}
                                                value={item.interestRate ?? (isCapitalising ? capitalisingLoanRateOf({ type: type.id }) : '')}
                                                onChange={(v) => updateLiability(type.id, item.id, 'interestRate', v)}
                                            />
                                            {isTermLoan && (
                                                <>
                                                    <NumberInput label="Remaining Loan Term (Years)" name="loanTerm" id={`liability-loanTerm-${item.id}`} value={item.loanTerm || ''} onChange={(v) => updateLiability(type.id, item.id, 'loanTerm', v)} />
                                                    <FormSelect label="Loan Type" name="loanType" id={`liability-loanType-${item.id}`} value={item.loanType || 'pi'} onChange={(n, v) => updateLiability(type.id, item.id, n, v)}>
                                                        <option value="pi">Principal & Interest</option>
                                                        <option value="io">Interest Only</option>
                                                    </FormSelect>
                                                </>
                                            )}
                                            {!isCapitalising && (
                                            <FormSelect label="Repayment Frequency" name="frequency" id={`liability-frequency-${item.id}`} value={item.frequency || 'monthly'} onChange={(n, v) => updateLiability(type.id, item.id, n, v)}>
                                                <option value="weekly">Weekly</option>
                                                <option value="fortnightly">Fortnightly</option>
                                                <option value="monthly">Monthly</option>
                                            </FormSelect>
                                            )}
                                            {isTermLoan && (
                                                <FormRadioGroup label="Repayment Amount" name="repaymentCalculation" value={item.repaymentCalculation || 'auto'} onChange={(n,v) => updateLiability(type.id, item.id, n, v)} options={[{label: 'Auto-Calculate', value: 'auto'}, {label: 'Enter Manually', value: 'manual'}]} />
                                            )}
                                            {/* One condition drives the value, the disabled state and the switch
                                                above — they disagreed before, which is what let the box look
                                                editable while discarding what was typed into it. */}
                                            {!isCapitalising && (
                                            <CurrencyInput
                                                label="Repayment Amount"
                                                name="repayments"
                                                id={`liability-repayments-${item.id}`}
                                                value={autoRepaymentApplies ? calculatedRepayment.toFixed(2) : (item.repayments || '')}
                                                onChange={(v) => updateLiability(type.id, item.id, 'repayments', v)}
                                                disabled={autoRepaymentApplies}
                                            />
                                            )}
                                            {isCapitalising && (
                                                <div className="md:col-span-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
                                                    <p className="text-xs text-slate-600 leading-relaxed">
                                                        <strong>No repayments.</strong> The interest is added to the balance each
                                                        year and the loan is repaid when the property is sold, so there is nothing
                                                        to pay from {voice.possessive} income. We grow the balance using the rate
                                                        above{type.id === 'homeEquityAccessScheme' ? ' — the Home Equity Access Scheme rate is set by the government (currently 3.95%)' : ''}.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    )
                                })}
                            </div>
                        </fieldset>
                    )
                ))}
            </div>
        </div>
    );
};

interface Step7IncomeExpensesProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    isPartnered: boolean;
    voice: ApplicantVoice;
}

const Step7_IncomeExpenses = ({ factFind, updateFactFind, isPartnered, voice }: Step7IncomeExpensesProps) => {
    const [showBudgetCalculator, setShowBudgetCalculator] = useState(false);
    const [showGiftingContext, setShowGiftingContext] = useState(false);
    // When the client isn't sure, we drop in the ASFA "Comfortable" benchmark (overridable). Track it so we can show the
    // assumption note, and clear it the moment they edit the figure themselves.
    const [assumedFromAsfa, setAssumedFromAsfa] = useState(false);
    const asfaEstimate = (isPartnered ? ASFA_BUDGET.couple : ASFA_BUDGET.single).total;

    const { income = {}, expenses = {} } = factFind;

    // Granny-flat deprivation (from Step 4) is auto-calculated and shown read-only here — one source of truth, no re-entry.
    const grannyFlatDep = getGrannyFlatDeprivationFromFactFind(factFind as unknown as FactFind, PENSION_RATES.couple);
    const grannyFlatGift = grannyFlatDep && grannyFlatDep.deprivation > 0 ? grannyFlatDep : null;

    const addIncome = () => {
        const newIncomes = [...(income.otherIncomes || []), {
            id: newEntityId(),
            type: 'salary',
            description: '',
            amount: '',
            ownership: 'client',
            isTaxable: true,
            isAssessable: true,
        }];
        updateFactFind('income', 'otherIncomes', newIncomes);
    };

    /**
     * A defined benefit pension's fund preset: picking the fund settles every rule the engine turns on
     * (definedBenefitPension.ts, 15 Oct 2026), so the adviser enters a payment and a tax-free figure
     * and nothing else. A later explicit answer on the row overrides any of these.
     */
    const withDefinedBenefitPreset = (inc: Record<string, unknown>, fundId: unknown) => {
        const preset = definedBenefitFundPreset(fundId);
        return {
            ...inc,
            dbFund: preset.id,
            dbIndexed: preset.indexed,
            dbMilitary: preset.military,
            dbPreSept1998Product: preset.preSept1998Product,
            dbTaxedSource: preset.taxedSource,
            dbReversionPct: preset.reversionPct ?? undefined,
        };
    };

    const updateIncome = (id: string | undefined, field: string, value: unknown) => {
        const updatedIncomes = (income.otherIncomes || []).map(inc => {
            if (inc.id === id) {
                let newInc: Record<string, unknown> = { ...inc, [field]: value };
                // If type changes, update amount for auto-calculated types
                if (field === 'type') {
                    const incomeType = OTHER_INCOME_TYPES.find(t => t.id === value);
                    if (incomeType && !incomeType.manualEntry) {
                        newInc.amount = incomeType.rate;
                    }
                    if (value === 'definedBenefitPension') {
                        // Nothing pre-filled (owner, 15 Oct 2026): the fund is chosen, then its preset applies.
                        newInc = { ...newInc, frequency: newInc.frequency || 'fortnightly', amount: '' };
                        if (newInc.dbFund) newInc = withDefinedBenefitPreset(newInc, newInc.dbFund);
                    }
                }
                if (field === 'dbFund') newInc = withDefinedBenefitPreset(newInc, value);
                // The annual figure the assessment reads, from the payment as it appears on the advice.
                if (newInc.type === 'definedBenefitPension' && (field === 'dbPaymentAmount' || field === 'frequency' || field === 'dbFund' || field === 'type')) {
                    newInc.amount = annualFromPayment(newInc.dbPaymentAmount, newInc.frequency);
                }
                return newInc as typeof inc;
            }
            return inc;
        });
        updateFactFind('income', 'otherIncomes', updatedIncomes);
    };

    const removeIncome = (id: string | undefined) => {
        const filteredIncomes = (income.otherIncomes || []).filter(inc => inc.id !== id);
        updateFactFind('income', 'otherIncomes', filteredIncomes);
    };

    const addCapitalExpense = () => {
        const newExpenses = [...(expenses.capitalExpenses || []), { id: newEntityId(), description: '', amount: '', year: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` }];
        updateFactFind('expenses', 'capitalExpenses', newExpenses);
    };

    const updateCapitalExpense = (id: string | undefined, field: string, value: unknown) => {
        const updatedExpenses = (expenses.capitalExpenses || []).map(exp => exp.id === id ? { ...exp, [field]: value } : exp);
        updateFactFind('expenses', 'capitalExpenses', updatedExpenses);
    };

    const removeCapitalExpense = (id: string | undefined) => {
        const filteredExpenses = (expenses.capitalExpenses || []).filter(exp => exp.id !== id);
        updateFactFind('expenses', 'capitalExpenses', filteredExpenses);
    };

    const handleBudgetSave = (total: number) => {
        updateFactFind('expenses', 'livingExpenses', total);
        setShowBudgetCalculator(false);
    };

    return (
        <div>
            <StepHeader icon={Banknote} step={7} title="Other Income & Expenses" />

            {/* Other Income Section */}
            <fieldset className="mb-8">
                <legend className="text-lg font-semibold text-gray-700 mb-4">Other Income</legend>
                <p className="text-sm text-gray-600 mb-4">Add any other sources of income not already captured from {voice.possessive} assets (e.g., salaries, carer allowance, overseas pensions).</p>
                <div className="space-y-4">
                    {(income.otherIncomes || []).map(inc => {
                        const incomeType = OTHER_INCOME_TYPES.find(t => t.id === inc.type);
                        return (
                            <div key={inc.id} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <FormSelect label="Type" name="type" value={inc.type || 'salary'} onChange={(n, v) => updateIncome(inc.id, n, v)}>
                                        {OTHER_INCOME_TYPES.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                                    </FormSelect>
                                    <FormInput label="Description" name="description" value={inc.description} onChange={(n, v) => updateIncome(inc.id, n, v)} />
                                    {inc.type === 'definedBenefitPension'
                                        ? (isPartnered
                                            ? <FormSelect label="Whose pension is it?" name="ownership" value={inc.ownership || 'client'} onChange={(n, v) => updateIncome(inc.id, n, v)}>
                                                <option value="client">{factFind.client?.firstName || 'Client'}</option>
                                                <option value="partner">{factFind.partner?.firstName || 'Partner'}</option>
                                            </FormSelect>
                                            : <div />)
                                        : <CurrencyInput label="Annual Amount ($)" name="amount" value={inc.amount} onChange={(v) => updateIncome(inc.id, 'amount', v)} disabled={!incomeType?.manualEntry} />}
                                </div>
                                {inc.type === 'definedBenefitPension' && (() => {
                                    /*
                                     * A DEFINED BENEFIT PENSION (owner, 15 Oct 2026): at most five questions, every rule
                                     * applied behind them. The fund presets the rules; the tax-free part comes off the
                                     * fund's Centrelink schedule; the panel beside says what Centrelink and the ATO do
                                     * with it — the user never sees a rule they have to apply. Nothing is pre-filled
                                     * until the fund is chosen, and a bought annuity recorded under Assets is pointed
                                     * out so its payments are not entered twice.
                                     */
                                    const fundChosen = !!inc.dbFund;
                                    const preset = definedBenefitFundPreset(inc.dbFund);
                                    const ownerIsPartner = inc.ownership === 'partner';
                                    const owner = ownerIsPartner ? factFind.partner : factFind.client;
                                    const other = ownerIsPartner ? factFind.client : factFind.partner;
                                    const ownerName = owner?.firstName || (ownerIsPartner ? 'your partner' : 'you');
                                    const partnerName = isPartnered ? (other?.firstName || 'your partner') : null;
                                    const annual = Number(inc.amount) || 0;
                                    const annuities = ((factFind.assets?.annuities || []) as Array<{ description?: string; income?: number; ownership?: string }>)
                                        .filter((a) => !isPartnered || !a.ownership || a.ownership === (inc.ownership || 'client'));
                                    const sentences = fundChosen && annual > 0
                                        ? describeDefinedBenefitTreatment(inc as OtherIncomeItem, { ownerName, ownerAge: calculateAge(owner?.dob), partnerName, cpiPct: DEFAULT_DB_INDEXATION_PCT, money: formatCurrency })
                                        : [];
                                    return (
                                        <div className="mt-4 space-y-4" data-testid="defined-benefit-fields">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <FormSelect label="Which fund pays it?" name="dbFund" value={inc.dbFund || ''} onChange={(n, v) => updateIncome(inc.id, n, v as DefinedBenefitFundId)}>
                                                    <option value="">Choose the fund…</option>
                                                    {DEFINED_BENEFIT_FUNDS.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                                </FormSelect>
                                                <CurrencyInput label="Amount per payment ($)" name="dbPaymentAmount" value={inc.dbPaymentAmount} onChange={(v) => updateIncome(inc.id, 'dbPaymentAmount', v)} />
                                                <FormSelect label="Paid" name="frequency" value={inc.frequency || 'fortnightly'} onChange={(n, v) => updateIncome(inc.id, n, v)}>
                                                    <option value="weekly">Weekly</option>
                                                    <option value="fortnightly">Fortnightly</option>
                                                    <option value="monthly">Monthly</option>
                                                    <option value="annually">Once a year</option>
                                                </FormSelect>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                {annual > 0 ? <>That is <strong>{formatCurrency(annual)}</strong> a year. </> : null}
                                                Not for an annuity bought with a lump sum — that belongs under Assets, where its payments are already counted.
                                            </p>
                                            {annuities.length > 0 && (
                                                <p className="rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white px-3 py-2 text-xs text-slate-700" data-testid="defined-benefit-annuity-warning">
                                                    {ownerName} already has an annuity under Assets ({annuities.map((a) => `${a.description || 'Annuity'}${Number(a.income) > 0 ? `, ${formatCurrency(Number(a.income))} a year` : ''}`).join('; ')}). Its payments are counted from there. Enter a pension here only if it is a different one.
                                                </p>
                                            )}
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <CurrencyInput label="Tax-free part (a year) ($)" name="dbDeductibleAmount" value={inc.dbDeductibleAmount} onChange={(v) => updateIncome(inc.id, 'dbDeductibleAmount', v)} />
                                                <p className="md:col-span-2 self-end text-xs text-slate-500">Printed on the fund's Centrelink schedule and annual statement as the deductible amount or tax-free component. Leave it blank and the whole payment counts as income.</p>
                                            </div>
                                            {fundChosen && (
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    <FormCheckbox label="Rises with inflation each year" name="dbIndexed" checked={inc.dbIndexed ?? preset.indexed} onChange={(n, v) => updateIncome(inc.id, n, v)} />
                                                    {isPartnered && (
                                                        <PercentageInput label={`${partnerName}'s share if ${ownerName} dies first (%)`} name="dbReversionPct" value={inc.dbReversionPct ?? preset.reversionPct ?? ''} onChange={(v) => updateIncome(inc.id, 'dbReversionPct', v)} />
                                                    )}
                                                    {inc.dbFund === 'other' && (
                                                        <FormSelect label="Offered before 20 September 1998?" name="dbPreSept1998Product" value={inc.dbPreSept1998Product ? 'yes' : 'no'} onChange={(n, v) => updateIncome(inc.id, n, v === 'yes')}>
                                                            <option value="no">No, or not sure</option>
                                                            <option value="yes">Yes</option>
                                                        </FormSelect>
                                                    )}
                                                    {inc.dbFund === 'other' && (
                                                        <FormSelect label="Paid from an untaxed source?" name="dbTaxedSource" value={inc.dbTaxedSource === false ? 'untaxed' : 'taxed'} onChange={(n, v) => updateIncome(inc.id, n, v !== 'untaxed')}>
                                                            <option value="taxed">No — a taxed source (most funds)</option>
                                                            <option value="untaxed">Yes — a government scheme</option>
                                                        </FormSelect>
                                                    )}
                                                </div>
                                            )}
                                            {inc.dbFund === 'other' && (
                                                <p className="text-xs text-slate-500">The fund's payment summary says which: an untaxed source shows an "untaxed element" and is taxed after 60 with a 10% offset; a taxed source is tax-free from 60.</p>
                                            )}
                                            {fundChosen && <p className="text-xs text-slate-500">{preset.note}</p>}
                                            {sentences.length > 0 && (
                                                <InsightCard tone="info" heading="How Centrelink and the ATO treat this pension">
                                                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700" data-testid="defined-benefit-treatment">
                                                        {sentences.map((line, i) => <li key={i}>{line}</li>)}
                                                    </ul>
                                                </InsightCard>
                                            )}
                                        </div>
                                    );
                                })()}
                                {inc.type === 'boarders' && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                        <FormSelect label="What they get" name="boardType" value={inc.boardType || 'lodging'} onChange={(n, v) => updateIncome(inc.id, n, v)}>
                                            <option value="lodging">A room only</option>
                                            <option value="bedAndBreakfast">A room and breakfast</option>
                                            <option value="board">A room and meals (full board)</option>
                                        </FormSelect>
                                        <p className="md:col-span-2 self-end text-xs text-slate-500">Centrelink counts only part of what a boarder pays: 70% for a room only, 50% with breakfast, 20% with full board. Leave out anything paid by {voice.possessive} parent, child or sibling — it is not counted.</p>
                                    </div>
                                )}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 items-center">
                                    <div>
                                    {isPartnered && inc.type !== 'definedBenefitPension' && (
                                        <FormSelect label="Owner" name="ownership" value={inc.ownership || 'client'} onChange={(n, v) => updateIncome(inc.id, n, v)}>
                                            <option value="client">{factFind.client?.firstName || 'Client'}</option>
                                            <option value="partner">{factFind.partner?.firstName || 'Partner'}</option>
                                            <option value="joint">Joint</option>
                                        </FormSelect>
                                    )}
                                    </div>
                                    <div className="flex items-center space-x-4 pt-6">
                                        {incomeType?.isCustom && (
                                            <>
                                                <FormCheckbox label="Taxable?" name="isTaxable" checked={inc.isTaxable} onChange={(n,v) => updateIncome(inc.id, n, v)} />
                                                <FormCheckbox label="Assessable?" name="isAssessable" checked={inc.isAssessable} onChange={(n,v) => updateIncome(inc.id, n, v)} />
                                            </>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-end gap-2 pt-6">
                                        {incomeType?.tooltip && <InfoIcon text={incomeType.tooltip} />}
                                        <button onClick={() => removeIncome(inc.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full transition-all duration-300 hover:scale-110"><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <button onClick={addIncome} className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200">
                    <PlusCircle size={20} /> Add Income Source
                </button>
            </fieldset>

            {/* Expenses Section */}
            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">Living & Capital Expenses</legend>

                {/* General Living Expenses */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 mb-6">
                    <p className="font-medium text-gray-800 mb-1">General Living Expenses (Annual)</p>
                    <p className="text-sm text-gray-500 mb-3">
                        Your best estimate of what {voice.subject} {voice.verb('spend', 'spends')} to live <strong>now</strong> — {voice.possessive} current, day-to-day costs,
                        before any move into aged care or other change in living arrangement. We&apos;ll factor that change in later.
                    </p>
                    <FormRadioGroup
                        label=""
                        name="expenseMode"
                        value={expenses.expenseMode || 'total'}
                        onChange={(n, v) => updateFactFind('expenses', n, v)}
                        options={[{ label: 'Enter Total Amount', value: 'total' }, { label: 'Use Budget Calculator', value: 'calculator' }]}
                    />
                    {expenses.expenseMode === 'total' ? (
                        <>
                            <CurrencyInput className="mt-4" label="Total Annual Living Expenses ($)" name="livingExpenses" value={expenses.livingExpenses || ''} onChange={(v) => { updateFactFind('expenses', 'livingExpenses', v); setAssumedFromAsfa(false); }} />
                            <button
                                type="button"
                                onClick={() => { updateFactFind('expenses', 'livingExpenses', asfaEstimate); setAssumedFromAsfa(true); }}
                                className="mt-2 text-sm font-semibold text-blue-600 hover:text-blue-800"
                            >
                                Not sure? Use a typical estimate
                            </button>
                            {assumedFromAsfa && (
                                <p className="mt-2 text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg p-3">
                                    We&apos;ve assumed an average for a {isPartnered ? 'couple' : 'single retiree'} ({formatCurrency(asfaEstimate)} a year,
                                    based on the ASFA &apos;Comfortable&apos; retirement standard). You can change this at any time.
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="mt-4">
                            <button onClick={() => setShowBudgetCalculator(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-100 text-teal-800 font-semibold rounded-lg hover:bg-teal-200">
                                <Calculator size={20} /> Open Budget Calculator
                            </button>
                            <p className="text-lg font-bold mt-2">Calculated Total: {formatCurrency(expenses.livingExpenses)}</p>
                        </div>
                    )}
                </div>

                {/* Capital Expenses */}
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-gray-800">One-off Capital Expenses</span>
                        <InfoIcon text="Enter the amount in the dollars of the expected expense date (the cost you expect in that month/year). Projections use this nominal figure in the strategy year that contains that date—they do not apply CPI or other indexation to this line for you." />
                    </div>
                    <div className="text-sm text-gray-600 mb-4">
                        Add any large, one-off expenses {voice.subject} {voice.verb('expect', 'expects')} (e.g., new car, major holiday).
                        <InfoIcon text={`Please do not include costs associated with ${voice.possessive} living arrangement choices (like aged care entry fees, retirement village costs, or home care package fees) as these will be modelled separately in other sections.`} />
                    </div>
                    <div className="space-y-4">
                        {(expenses.capitalExpenses || []).map(exp => (
                            <div key={exp.id} className="flex flex-wrap gap-4 items-end">
                                <div className="flex-grow" style={{minWidth: '200px'}}>
                                    <FormInput label="Description" name="description" value={exp.description} onChange={(n, v) => updateCapitalExpense(exp.id, n, v)} />
                                </div>
                                <div className="flex-grow" style={{minWidth: '120px'}}>
                                    <CurrencyInput label="Amount ($)" name="amount" value={exp.amount} onChange={(v) => updateCapitalExpense(exp.id, 'amount', v)} />
                                </div>
                                <div className="flex-grow" style={{minWidth: '150px'}}>
                                     <FormInput label="Expected Date" name="year" type="month" value={exp.year} onChange={(n, v) => updateCapitalExpense(exp.id, n, v)} />
                                </div>
                                <div>
                                    <button onClick={() => removeCapitalExpense(exp.id)} className="p-2 text-red-500 hover:bg-red-100 rounded-full mb-1 transition-all duration-300 hover:scale-110"><Trash2 size={18} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button onClick={addCapitalExpense} className="mt-4 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                        <PlusCircle size={20} /> Add Capital Expense
                    </button>
                </div>
            </fieldset>

            {/* Gifting History Section */}
            <fieldset className="mt-8">
                <legend className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    Asset Gifting History
                    <InfoIcon text="Money or assets that have previously been gifted may impact age pension entitlements if they are over certain thresholds" />
                </legend>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    {grannyFlatGift && (
                        <div className="mb-4 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-4">
                            <p className="text-sm text-slate-700">
                                <strong>From {voice.possessive} granny flat arrangement (Step 4):</strong> {formatCurrency(grannyFlatGift.deprivation)} is treated as a gift in FY{grannyFlatGift.establishmentFyEnd}, calculated automatically from what {voice.pronoun} paid versus the assessed value of the arrangement. It's already counted in {voice.pronounPossessive} assessment for five years from then — just add any <em>other</em> gifts below.
                            </p>
                        </div>
                    )}
                    {/* A Yes/No, not a lone tick-box. Unticked used to mean BOTH "nothing was gifted"
                        and "nobody looked at this", which is why a reader asked his adviser whether he
                        should "just leave this blank" (client meeting, 2026-08-20). Answering No is now
                        a recorded answer, and the same pattern the liabilities step already uses. */}
                    <fieldset className="mb-4">
                        <legend className="text-gray-700 font-medium mb-3">
                            {voice.verb('Have', 'Has')} {voice.subject} gifted assets in the last five financial years?
                        </legend>
                        {/* A two-column grid, not a flex row: the two labels are different lengths, so
                            content-width buttons came out visibly mismatched. */}
                        <div className="grid grid-cols-2 gap-3 max-w-md">
                            {([
                                { id: 'yes', label: 'Yes — there have been gifts', gifted: true },
                                { id: 'no', label: 'No — nothing gifted', gifted: false },
                            ] as const).map(({ id, label, gifted }) => {
                                const chosen = gifted
                                    ? !!factFind.giftsHistory?.hasGifted
                                    : !factFind.giftsHistory?.hasGifted && !!factFind.giftsHistory?.confirmedNone;
                                return (
                                    <label key={id} className="block cursor-pointer">
                                        <input
                                            type="radio"
                                            name="hasGifted"
                                            value={id}
                                            checked={chosen}
                                            onChange={() => {
                                                updateFactFind('giftsHistory', 'hasGifted', gifted);
                                                updateFactFind('giftsHistory', 'confirmedNone', !gifted);
                                            }}
                                            className="peer sr-only"
                                        />
                                        <span className="flex h-full items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-4 py-2.5 text-center text-sm font-medium text-gray-700 transition-all hover:border-slate-300 peer-checked:border-[#0F52E0] peer-checked:bg-blue-50 peer-checked:text-blue-900 peer-focus-visible:ring-2 peer-focus-visible:ring-[#0290F5] peer-focus-visible:ring-offset-2">
                                            {label}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </fieldset>

                    {(factFind.giftsHistory?.hasGifted) && (
                        <div className="ml-8 mt-4 space-y-4">
                            <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                                <button
                                    onClick={() => setShowGiftingContext(!showGiftingContext)}
                                    className="w-full p-4 text-left flex items-center justify-between hover:bg-blue-100 transition-colors duration-200"
                                >
                                    <div className="flex items-center">
                                        <Info className="text-blue-600 mr-3 flex-shrink-0" size={18} />
                                        <h4 className="text-blue-800 font-semibold text-sm">Gifting Calculation Context</h4>
                                    </div>
                                    <ChevronDown
                                        className={`text-blue-600 transition-transform duration-200 ${showGiftingContext ? 'rotate-180' : ''}`}
                                        size={16}
                                    />
                                </button>
                                {showGiftingContext && (
                                    <div className="px-4 pb-4">
                                        <p className="text-blue-700 text-sm leading-relaxed">
                                            Combined limits (couple or single) are typically up to <strong>{formatCurrency(GIFTING_LIMIT_ANNUAL)} per financial year</strong> (1 July-30 June) and up to <strong>{formatCurrency(GIFTING_LIMIT_5_YEAR)} over any rolling five financial years</strong>, without deprivation. Amounts over those free areas may be treated as <strong>deprived assets</strong> (still counted in {voice.possessive} assets test, usually for five years from the gift). Enter totals below per <strong>FY ending 30 June</strong> (the year in the label is the June year).
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-3">
                                {[0, 1, 2, 3, 4].map(yearOffset => {
                                    const year = new Date().getFullYear() - yearOffset;
                                    const giftKey = `year${year}`;
                                    return (
                                        // Stacks below `sm`. The row used to be a fixed 192px
                                        // non-shrinking label + a 128px input + trailing text —
                                        // about 440px of unbreakable content in a card roughly
                                        // 358px wide on a phone, so "total gift value" ran off the
                                        // right edge with no way to scroll to it (2026-08-19).
                                        <div key={year} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                                            <label className="w-full sm:w-48 text-sm font-medium text-gray-700 sm:shrink-0">FY ending June {year}:</label>
                                            <div className="flex items-center gap-3 min-w-0">
                                                <CurrencyInput
                                                    label=""
                                                    value={(factFind.giftsHistory?.[giftKey] as number | undefined) ?? ''}
                                                    onChange={(v) => updateFactFind('giftsHistory', giftKey, v ?? 0)}
                                                    className="w-32 shrink-0"
                                                    placeholder="$0"
                                                />
                                                <span className="text-sm text-gray-600">total gift value</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {(() => {
                                const gh = factFind.giftsHistory || {};
                                const totalGifts = Object.keys(gh)
                                    .filter((key) => key.startsWith('year'))
                                    .reduce((sum, key) => sum + (Number(gh[key]) || 0), 0);
                                const modeledDeprived = calculateDeprivedAssetsFromGifts(gh, { asOfDate: new Date() });
                                if (modeledDeprived > 0) {
                                    return (
                                        <div className="mt-4 p-3 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded">
                                            <p className="text-sm text-slate-700">
                                                <strong>Note:</strong> On the amounts entered (FY buckets), the tool includes about{' '}
                                                <strong>{formatCurrency(modeledDeprived)}</strong> as deprived assets for Age Pension assessment (combined annual and five-year free areas). Gifts totaling {formatCurrency(totalGifts)} were entered; Centrelink may still apply timing and other rules not modelled here.
                                            </p>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    )}
                </div>
            </fieldset>

            {showBudgetCalculator && (
                <BudgetCalculator
                    onClose={() => setShowBudgetCalculator(false)}
                    onSave={handleBudgetSave}
                    isPartnered={isPartnered}
                />
            )}
        </div>
    );
};

interface Step8FamilyEstateProps {
    factFind: DraftFactFind;
    updateFactFind: UpdateFactFind;
    isPartnered: boolean;
    voice: ApplicantVoice;
}

/**
 * What a non-binding nomination actually means, shown only when someone selects one.
 *
 * Deliberately NOT a warning. A non-binding nomination usually results in the money going exactly
 * where it was nominated — the trustee normally follows it — so telling a family their wishes will
 * be ignored would be both alarmist and wrong (owner's direction, 2026-08-22). What is true is that
 * the trustee decides, which is worth knowing and worth a conversation, and that is all this says.
 */
const NonBindingNote = ({ nomination }: { nomination?: string }) => {
    if (nomination !== 'non_binding') return null;
    return (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
            <p className="text-xs text-slate-600 leading-relaxed">
                With a non-binding nomination the super fund&apos;s trustee makes the final decision. In
                practice they usually follow the nomination, so this is not a problem to fix — it simply
                means the outcome is not guaranteed, and a binding nomination is the way to remove that
                discretion if certainty matters.
            </p>
        </div>
    );
};

const Step8_FamilyEstate = ({ factFind, updateFactFind, isPartnered, voice }: Step8FamilyEstateProps) => {
    const family = factFind.family || {};
    return (
    <div>
        <StepHeader icon={Users} step={8} title="Family & Estate Planning" />
        <div className="space-y-8">
             <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">Family Details</legend>
                 <NumberInput label={voice.isThirdPerson ? `How many children ${voice.verb('do', 'does')} ${voice.subject} have?` : 'Number of Children'} name="childrenCount" value={family.childrenCount || 0} onChange={(v) => updateFactFind('family', 'childrenCount', v)} />
            </fieldset>
            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">Legal & Health Directives</legend>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
                    <div>
                        <p className="font-medium text-gray-800 mb-2">{factFind.client?.firstName || 'the applicant'}</p>
                        <FormRadioGroup label="Will in place?" name="clientWill" value={family.clientWill || ''} onChange={(n,v) => updateFactFind('family', n, v)} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}, {label: 'Unsure', value: 'unsure'}]} />
                        <FormRadioGroup label="Enduring Power of Attorney?" name="clientEpoa" value={family.clientEpoa || ''} onChange={(n,v) => updateFactFind('family', n, v)} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}, {label: 'Unsure', value: 'unsure'}]} />
                        <FormRadioGroup label="Advanced Care Directive?" name="clientAcd" value={family.clientAcd || ''} onChange={(n,v) => updateFactFind('family', n, v)} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}, {label: 'Unsure', value: 'unsure'}]} />
                         <FormSelect label="Super Death Benefit Nomination" name="clientSuperNom" value={family.clientSuperNom || ''} onChange={(n,v) => updateFactFind('family', n, v)}>
                            <option value="">Select...</option>
                            <option value="binding">Binding</option>
                            <option value="non_binding">Non-Binding</option>
                            <option value="none">None in place</option>
                            {/* Most people do not know which kind they signed, and the other three
                                directives on this screen already allow "Unsure" — forcing a guess here
                                produces worse data than recording the uncertainty. */}
                            <option value="unsure">Unsure</option>
                        </FormSelect>
                        <NonBindingNote nomination={family.clientSuperNom} />
                    </div>
                    {isPartnered && (
                        <div>
                            <p className="font-medium text-gray-800 mb-2">{factFind.partner?.firstName || 'Partner'}</p>
                            <FormRadioGroup label="Will in place?" name="partnerWill" value={family.partnerWill || ''} onChange={(n,v) => updateFactFind('family', n, v)} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}, {label: 'Unsure', value: 'unsure'}]} />
                            <FormRadioGroup label="Enduring Power of Attorney?" name="partnerEpoa" value={family.partnerEpoa || ''} onChange={(n,v) => updateFactFind('family', n, v)} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}, {label: 'Unsure', value: 'unsure'}]} />
                             <FormRadioGroup label="Advanced Care Directive?" name="partnerAcd" value={family.partnerAcd || ''} onChange={(n,v) => updateFactFind('family', n, v)} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}, {label: 'Unsure', value: 'unsure'}]} />
                             <FormSelect label="Super Death Benefit Nomination" name="partnerSuperNom" value={family.partnerSuperNom || ''} onChange={(n,v) => updateFactFind('family', n, v)}>
                                <option value="">Select...</option>
                                <option value="binding">Binding</option>
                                <option value="non_binding">Non-Binding</option>
                                <option value="none">None in place</option>
                                <option value="unsure">Unsure</option>
                            </FormSelect>
                            <NonBindingNote nomination={family.partnerSuperNom} />
                        </div>
                    )}
                </div>
            </fieldset>
            <fieldset>
                <legend className="text-lg font-semibold text-gray-700 mb-4">Family Dynamics</legend>
                {/* Tri-state (owner, 23 Sep 2026): a seeded "No" put "no complex family dynamics" into the
                    advice document as though the client had said it. Both of these read `=== true`
                    everywhere, so unanswered behaves exactly as No did — what changes is that the
                    document no longer asserts an answer nobody gave. */}
                <FormRadioGroup label="Are there any complex family dynamics (e.g. blended families, estranged members)?" name="complexDynamics" value={toTriYesNo(family.complexDynamics)} onChange={(n,v) => updateFactFind('family', n, fromTriYesNo(v))} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
                <FormRadioGroup label={`${voice.verb('Have', 'Has')} ${voice.subject} provided significant financial assistance to one child over others that could cause issues?`} name="unevenAssistance" value={toTriYesNo(family.unevenAssistance)} onChange={(n,v) => updateFactFind('family', n, fromTriYesNo(v))} options={[{label: 'Yes', value: 'yes'}, {label: 'No', value: 'no'}]} />
               </fieldset>
        </div>
    </div>
    );
};

export {
    Step1_WhoIsCompleting,
    Step4_LivingSituation,
    Step2_YourDetails,
    Step3_GoalsCareNeeds,
    Step4_FamilyHome,
    Step4_AltLiving,
    Step5_Assets,
    Step6_Liabilities,
    Step7_IncomeExpenses,
    Step8_FamilyEstate,
};
